import { PLAN_TIERS, MIN_TOPUP_MILLICENTS } from '@lore/billing';
import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import { docsContract } from './docs.ts';
import { entitiesContract } from './entities.ts';
import { favoritesContract } from './favorites.ts';
import { mentionsContract } from './mentions.ts';
import { projectsContract } from './projects.ts';
import { regionsContract } from './regions.ts';
import { searchContract } from './search.ts';

// Real-time event spine (see apps/api/src/events/AGENTS.md). Re-exported
// here so consumers continue importing from '@lore/contracts'.
export * from './events/types.ts';

export const defaultThreadFileParseSizeLimitInBytes = 30 * 1024 * 1024;

// LLM-wiki entities — kind registry, per-kind payload schemas, and the
// payload-schema introspection helper.
export * from './entities/index.ts';

const c = initContract();

export const publicEmailDomains = [
  'gmail.com',
  'googlemail.com',
  'hotmail.com',
  'outlook.com',
  'live.com',
  'msn.com',
  'yahoo.com',
  'ymail.com',
  'rocketmail.com',
  'icloud.com',
  'me.com',
  'mac.com',
  'aol.com',
  'proton.me',
  'protonmail.com',
  'pm.me',
  'tutanota.com',
  'tuta.io',
  'fastmail.com',
  'hey.com',
  'mail.com',
  'gmx.com',
  'gmx.net',
  'zoho.com',
  'zohomail.com',
  'hushmail.com',
  'lycos.com',
  'inbox.com',
  'yandex.com',
  'yandex.ru',
  'mail.ru',
  'inbox.ru',
  'bk.ru',
  'list.ru',
  'rambler.ru',
  'qq.com',
  '163.com',
  '126.com',
  'yeah.net',
  'sina.com',
  'naver.com',
  'daum.net',
  'hanmail.net',
  'kakao.com',
  'rediffmail.com',
  'comcast.net',
  'verizon.net',
  'att.net',
  'sbcglobal.net',
  'bellsouth.net',
  'earthlink.net',
  'charter.net',
  'cox.net',
  'roadrunner.com',
  'spectrum.net',
  'btinternet.com',
  'virginmedia.com',
  'sky.com',
  'orange.fr',
  'wanadoo.fr',
  'free.fr',
  'laposte.net',
  'web.de',
  't-online.de',
  'gmx.de',
  'libero.it',
  'virgilio.it',
  'alice.it',
  'seznam.cz',
  'centrum.cz',
  'wp.pl',
  'interia.pl',
  'o2.pl',
  'onet.pl',
] as const;

const publicEmailDomainSet = new Set<string>(publicEmailDomains);

export function normalizeEmailDomain(value: string): string | null {
  const domain = value.includes('@') ? value.split('@').pop() : value;
  const normalized = domain?.trim().replace(/^@+/, '').toLowerCase() ?? '';
  return normalized.length > 0 && normalized.includes('.') ? normalized : null;
}

export function isPublicEmailDomain(value: string): boolean {
  const domain = normalizeEmailDomain(value);
  return domain ? publicEmailDomainSet.has(domain) : false;
}

export const messageSchema = z.object({
  id: z.string().uuid(),
  role: z.enum(['user', 'assistant']),
  content: z.string().min(1),
  timestamp: z.string().datetime(),
});

export const threadSummarySchema = z.object({
  id: z.string().uuid(),
  title: z.string().min(1),
  author: z.string().min(1),
  authorAvatar: z.string().min(1).max(4),
  model: z.string().min(1),
  turnCount: z.number().int().nonnegative(),
  createdAt: z.string().datetime(),
  preview: z.string(),
});

export const threadDetailsSchema = threadSummarySchema.extend({
  messages: z.array(messageSchema),
});

export const errorSchema = z.object({
  message: z.string(),
});

// Allowed handle alphabet. Restricted to the GitHub/Twitter-style subset
// so handles are URL-safe and unambiguous (no Unicode confusables, no
// dots/underscores adjacent to dashes). Validated case-insensitively at
// the application layer; the DB stores whatever casing the user typed.
export const handleRegex = /^[a-zA-Z0-9_-]{2,32}$/;

export const handleSchema = z
  .string()
  .regex(handleRegex, 'Handle must be 2–32 characters: letters, numbers, dashes, or underscores.');

/**
 * Closed set of answers to "How did you learn about Lore?" — captured at the
 * end of onboarding. `other` is a catch-all paired with `attribution_source_detail`.
 */
export const userAttributionSourceSchema = z.enum([
  // Personal / IRL
  'friend',
  'events',
  // Social
  'twitter',
  'linkedin',
  // Online communities
  'hacker_news',
  'reddit',
  'discord',
  'youtube',
  // Search
  'google',
  'ai_search',
  // Catch-all
  'other',
]);

export type UserAttributionSource = z.infer<typeof userAttributionSourceSchema>;

export const userSchema = z.object({
  id: z.string(),
  first_name: z.string().min(1),
  last_name: z.string().min(1),
  display_name: z.string().min(1),
  /**
   * Public-facing username (e.g. `molinar`). Null for users who haven't
   * picked one yet — the new profile page prompts on first visit.
   */
  handle: z.string().nullable(),
  bio: z.string().max(280).nullable(),
  /** Resolved (presigned or public) URL of the user's avatar, if any. */
  avatar_url: z.string().nullable(),
  /** Resolved URL of the user's banner image, if any. */
  banner_url: z.string().nullable(),
  share_new_threads_to_workspace: z.boolean(),
  experimental_features_enabled: z.boolean(),
  onboarded_at: z.string().nullable(),
  /**
   * User's self-reported answer to "How did you learn about Lore?", recorded
   * at the end of onboarding. Null when skipped or for pre-2c users.
   */
  attribution_source: userAttributionSourceSchema.nullable(),
  /** Free-text companion to `attribution_source === 'other'`. */
  attribution_source_detail: z.string().nullable(),
});

/**
 * PostHog person properties resolved server-side. Returned by
 * `/whoami` so the web client can call `posthog.identify()` with a
 * complete cohort-ready property set without an extra round trip
 * (plan_tier is otherwise behind `/billing/state`).
 */
export const posthogUserPropertiesSchema = z.object({
  email: z.string().nullable(),
  name: z.string().nullable(),
  handle: z.string().nullable(),
  created_at: z.string(),
  onboarded_at: z.string().nullable(),
  is_admin: z.boolean(),
  referred_by_user_id: z.string().nullable(),
  plan_tier: z.enum(['free', 'creator', 'team']),
  organization_id: z.string().nullable(),
  signup_source: z.enum(['referral', 'organic']),
  // Activation milestone timestamps. ISO-8601 strings when the user
  // has hit the milestone, null otherwise. Exposed on the person
  // profile so PostHog cohort filters can target activated users
  // without joining events to person properties.
  first_upload_at: z.string().nullable(),
  first_share_at: z.string().nullable(),
  // Onboarding-funnel activation watermarks (paired with the
  // `cli.activated` / `plugin.activated` events). `first_cli_auth_at`
  // is set the first time /api/whoami sees a CLI User-Agent;
  // `first_plugin_activated_at` is set on the first MCP `share_session`;
  // `first_plugin_agent` records the harness that drove the first
  // plugin activation. Exposed as person properties so PostHog cohorts
  // like "activated CLI within 24h of signup" can be expressed without
  // joining events.
  first_cli_auth_at: z.string().nullable(),
  first_plugin_activated_at: z.string().nullable(),
  first_plugin_agent: z.string().nullable(),
});

/**
 * PostHog group ('organization') properties + the group key the
 * client should pass to `posthog.group('organization', <key>)`. Null
 * when the viewer isn't currently scoped to an organization.
 */
export const posthogOrganizationContextSchema = z.object({
  group_key: z.string().min(1),
  properties: z.object({
    name: z.string(),
    plan_tier: z.enum(['free', 'creator', 'team']),
    seat_count: z.number().int().nonnegative(),
    created_at: z.string(),
    email_domains: z.array(z.string()),
    default_thread_visibility: z.string(),
    member_count: z.number().int().nonnegative().nullable(),
  }),
});

export const whoAmIResponseSchema = z.object({
  authProviderUserId: z.string().min(1).optional(),
  authProviderOrganizationId: z.string().min(1).optional(),
  org_name: z.string().min(1).optional(),
  workspaceOrganizationIds: z.array(z.string().min(1)).optional(),
  organizations: z.array(
    z.object({
      id: z.string().min(1),
      authProviderOrganizationId: z.string().min(1).optional(),
      name: z.string().min(1),
    }),
  ),
  emailAddress: z.string().email(),
  name: z.string().nullable(),
  user: userSchema,
  is_admin: z.boolean(),
  /**
   * True on the very first whoami after WorkOS provisioning created the
   * local Lore user row; false on every subsequent call (including
   * later sign-ins from the same account). Lets the web client fire
   * its own `signup.completed` so the funnel sits on the same `$lib`
   * as `signup.started` / `auth.form_submitted`. The server still fires
   * the authoritative `signup.completed` from the same whoami branch.
   */
  is_new_signup: z.boolean(),
  /**
   * PostHog identity context resolved server-side. The web client
   * calls `posthog.identify(distinctId, posthog.user_properties)`
   * and, when present, `posthog.group('organization',
   * posthog.organization.group_key, posthog.organization.properties)`.
   */
  posthog: z.object({
    user_properties: posthogUserPropertiesSchema,
    organization: posthogOrganizationContextSchema.nullable(),
  }),
});

export const cliAuthConfigResponseSchema = z.object({
  workos_client_id: z.string().min(1),
  workos_api_origin: z.string().url(),
});

export const updateCurrentUserRequestSchema = z
  .object({
    share_new_threads_to_workspace: z.boolean().optional(),
    experimental_features_enabled: z.boolean().optional(),
    /**
     * Mark the user as onboarded. Accept only `"now"` as a convenience —
     * the server stamps the actual timestamp. Allows null explicitly so
     * dev tooling can reset a user for re-onboarding without DB surgery.
     */
    onboarded_at: z.union([z.literal('now'), z.null()]).optional(),
    /**
     * "How did you learn about Lore?" answer captured at the end of onboarding.
     * Null clears a previously stored value.
     */
    attribution_source: userAttributionSourceSchema.nullable().optional(),
    /** Free-text detail; only meaningful when `attribution_source === 'other'`. */
    attribution_source_detail: z.string().max(280).nullable().optional(),
    display_name: z.string().min(1).max(120).optional(),
    handle: handleSchema.optional(),
    bio: z.string().max(280).nullable().optional(),
    /**
     * Storage URL produced by `POST /users/me/profile-image/upload-url`.
     * Pass `null` to clear the avatar/banner and fall back to the
     * generated default visual.
     */
    avatar_storage_url: z.string().min(1).nullable().optional(),
    banner_storage_url: z.string().min(1).nullable().optional(),
  })
  .refine((body) => Object.keys(body).length > 0, {
    message: 'At least one field must be provided',
  });

export const organizationMemberSchema = z.object({
  id: z.string(),
  display_name: z.string(),
  /** Public-facing handle, null until the member picks one. */
  handle: z.string().nullable(),
  /**
   * Resolved avatar URL (presigned download or null) — same source of
   * truth as the user's profile page and the AppHeader avatar.
   */
  avatar_url: z.string().nullable(),
  /**
   * Count of threads authored by this member that the requesting user can
   * currently see (private-to-requester threads + shared workspace threads +
   * any public threads from that member).
   */
  visible_thread_count: z.number().int().nonnegative(),
  /**
   * ISO timestamp of the member's most recent user-created block or
   * authenticated Lore web-app heartbeat in this organization. Null when
   * neither activity source exists yet.
   */
  last_active_at: z.string().datetime().nullable(),
  /**
   * ISO timestamp of the member's most recent authenticated Lore web-app
   * heartbeat in this organization. Null when they have not used the product
   * in this workspace yet.
   */
  product_last_seen_at: z.string().datetime().nullable(),
});

// Public profile of any user. Returned by GET /users/:id with optional
// auth: unauthenticated viewers see only the user's public-thread count,
// signed-in viewers see workspace + public + their own threads by that
// author — matching exactly what listThreads would surface if filtered
// by `author_ids`. We deliberately omit email, first/last name, workos_id,
// and onboarded_at so the profile endpoint never leaks PII beyond what
// already ships in thread metadata.
export const userProfileResourceSchema = z.object({
  id: z.string().min(1),
  display_name: z.string().min(1),
  /** Public username — null when the user hasn't picked one yet. */
  handle: z.string().nullable(),
  bio: z.string().max(280).nullable(),
  avatar_url: z.string().nullable(),
  banner_url: z.string().nullable(),
  organization_name: z.string().min(1).nullable(),
  /** ISO timestamp of when the account was created. */
  created_at: z.string().min(1),
  /**
   * Count of non-deleted threads authored by this user that the viewer
   * can access right now, restricted to threads that have at least one
   * user message (matches listThreads's `hasUserBlock` filter so the
   * count lines up with the rendered list). Zero for unauthenticated
   * viewers when the user has no public threads.
   */
  visible_thread_count: z.number().int().nonnegative(),
  follower_count: z.number().int().nonnegative(),
  following_count: z.number().int().nonnegative(),
  published_project_count: z.number().int().nonnegative(),
  /** True when the authenticated viewer follows this user. */
  is_following: z.boolean(),
  /** True when the resource refers to the authenticated viewer. */
  is_self: z.boolean(),
});

export const organizationMemberListResponseSchema = z.object({
  type: z.literal('list'),
  list_type: z.literal('organization_member'),
  objects: z.array(organizationMemberSchema),
});

export const createOrganizationInviteRequestSchema = z.object({
  email_address: z.string().email(),
});

export const createOrganizationInviteResponseSchema = z.object({
  email_address: z.string().email(),
  /** WorkOS invitation id created on successful invite. */
  invitation_id: z.string().min(1),
});

export const ensureWorkOSOrganizationResponseSchema = z.object({
  organization_id: z.string().min(1),
  organization_name: z.string().min(1),
  domain: z.string().min(1),
  created: z.boolean(),
});

export const createWaitlistEntryRequestSchema = z.object({
  location: z.string().trim().min(1),
  contact: z.string().trim().email(),
});

export const submitContactMessageRequestSchema = z.object({
  name: z.string().trim().min(1).max(100).describe("Sender's name"),
  email: z.string().trim().email().max(200).describe('Reply-to email'),
  message: z.string().trim().min(1).max(5000).describe('Message body'),
  // Honeypot: a hidden field real users never fill. A non-empty value
  // marks the submission as spam — the server accepts it (200) but does
  // not send any email. Optional so legitimate clients can omit it.
  company: z.string().max(200).optional(),
});

export const submitContactMessageResponseSchema = z.object({
  ok: z.boolean(),
});

export const waitlistEntrySchema = z.object({
  location: z.string().min(1),
  contact: z.string().email(),
  created_at: z.string().datetime(),
});

export const feedbackTypeSchema = z.enum([
  'general',
  'bug',
  'feature_request',
  'question',
]);

export const createFeedbackRequestSchema = z.object({
  type: feedbackTypeSchema,
  message: z.string().trim().min(1).max(5000),
});

export const feedbackEntrySchema = z.object({
  id: z.string().min(1),
  type: feedbackTypeSchema,
  message: z.string().min(1),
  created_at: z.string().datetime(),
});

export const createBuildingBlockSnapshotSchema = z.object({
  path_glob: z.string().min(1),
  name: z.string().min(1),
  description: z.string().min(1),
});

export const createBuildingBlockSnapshotsRequestSchema = z.object({
  repo_origin_path: z.string().min(1),
  building_blocks: z.array(createBuildingBlockSnapshotSchema),
});

const planIdSchema = z.string().regex(/^th_[0-9A-Za-z]{22}$/);
const planRevisionIdSchema = z.string().regex(/^tb_[0-9A-Za-z]{22}$/);

export const planListObjectSchema = z.object({
  id: planIdSchema,
  author: z.object({
    id: z.string().min(1),
    display_name: z.string().min(1),
  }),
  // Server-derived plain-text preview for list cards (TAN-4244 / BE-5):
  // computed from the latest revision's Prosemirror JSON so the list
  // endpoint never has to read the full markdown body from storage.
  title: z.string().min(1),
  excerpt: z.string(),
  created_at: z.string().datetime(),
  repo_origin_path: z.string().min(1),
  latest_revision_id: planRevisionIdSchema,
  revision_count: z.number().int().positive(),
});

export const planCommentAuthorSchema = z.object({
  id: z.string().min(1),
  display_name: z.string().min(1),
});

export const planCommentSchema = z.object({
  id: z.string().min(1),
  author_id: z.string().min(1).nullable(),
  author: planCommentAuthorSchema.nullable(),
  created_at: z.string().datetime(),
  deleted_at: z.string().datetime().nullable(),
  content: z.string().min(1),
});

export const planCommentThreadSchema = z.object({
  id: z.string().min(1),
  thread_block_id: z.string().min(1),
  created_at: z.string().datetime(),
  resolved_at: z.string().datetime().nullable(),
  comments: z.array(planCommentSchema),
});

export const createPlanCommentThreadRequestSchema = z.object({
  thread_block_id: planRevisionIdSchema,
  from_position: z.number().int().positive(),
  to_position: z.number().int().positive(),
  selected_text: z.string().min(1),
  content: z.string().trim().min(1),
});

export const createPlanCommentThreadResponseSchema = z.object({
  id: z.string().min(1),
});

export const createThreadBlockCommentThreadRequestSchema = z.object({
  content: z.string().trim().min(1),
});

export const createThreadBlockCommentThreadResponseSchema = z.object({
  id: z.string().min(1),
});

export const createPlanCommentRequestSchema = z.object({
  content: z.string().trim().min(1),
});

export const createPlanCommentResponseSchema = z.object({
  id: z.string().min(1),
});

export const createThreadBlockCommentRequestSchema = z.object({
  content: z.string().trim().min(1),
});

export const createThreadBlockCommentResponseSchema = z.object({
  id: z.string().min(1),
});

export const updatePlanCommentThreadRequestSchema = z.object({
  resolved: z.boolean(),
});

export const updatePlanCommentThreadResponseSchema = z.object({
  id: z.string().min(1),
  resolved_at: z.string().datetime().nullable(),
});

export const updateThreadBlockCommentThreadRequestSchema = z.object({
  resolved: z.boolean(),
});

export const updateThreadBlockCommentThreadResponseSchema = z.object({
  id: z.string().min(1),
  resolved_at: z.string().datetime().nullable(),
});

export const prosemirrorJsonSchema = z.record(z.string(), z.unknown());

export const planRevisionSchema = z.object({
  id: planRevisionIdSchema,
  // TAN-4244 / BE-5: only the latest revision's body and prosemirror_json
  // are populated in `GET /api/plans/:id`. Older revisions ship as
  // metadata-only stubs (`body: null`, `prosemirror_json: null`) and the
  // client fetches their content on demand via
  // `GET /api/plans/:id/revisions/:revisionId`. This keeps the detail
  // path to a single object-storage read regardless of how many
  // revisions a plan has accumulated.
  body: z.string().nullable(),
  created_at: z.string().datetime(),
  prosemirror_json: prosemirrorJsonSchema.nullable(),
  comment_threads: z.array(planCommentThreadSchema),
});

export const getPlanRevisionResponseSchema = planRevisionSchema.extend({
  // The dedicated revision endpoint always populates body +
  // prosemirror_json. Re-narrowing here gives callers a tighter type at
  // the boundary without forcing them to null-check.
  body: z.string(),
  prosemirror_json: prosemirrorJsonSchema,
});

export const getPlanResponseSchema = z.object({
  id: planIdSchema,
  created_at: z.string().datetime(),
  repo_origin_path: z.string().min(1),
  author: z.object({
    id: z.string().min(1),
    display_name: z.string().min(1),
  }),
  latest_revision_id: planRevisionIdSchema,
  revision_count: z.number().int().positive(),
  revisions: z.array(planRevisionSchema),
});

export const planListResponseSchema = z.object({
  type: z.literal('list'),
  list_type: z.literal('plan'),
  has_more: z.boolean(),
  objects: z.array(planListObjectSchema),
});

export const listPlansQuerySchema = z.object({
  before: z.string().min(1).optional(),
  after: z.string().min(1).optional(),
  author_id: z.string().min(1).optional(),
  repo_origin_path: z.string().min(1).optional(),
});

export const buildingBlockSnapshotSchema = z.object({
  id: z.string().min(1),
  organization_id: z.string().min(1).nullable(),
  created_at: z.string().min(1),
  repo_origin_path: z.string().min(1),
  path_glob: z.string().min(1),
  name: z.string().min(1),
  description: z.string().min(1),
});

export const createBuildingBlockSnapshotsResponseSchema = z.array(buildingBlockSnapshotSchema);

export const buildingBlockSnapshotListResponseSchema = z.object({
  type: z.literal('list'),
  list_type: z.literal('building_block'),
  has_more: z.boolean(),
  objects: z.array(buildingBlockSnapshotSchema),
});

export const listBuildingBlockSnapshotsQuerySchema = z.object({
  repo_origin_path: z.string().min(1),
  before: z.string().min(1).optional(),
  after: z.string().min(1).optional(),
});

export const repositorySourceSchema = z.enum(['thread', 'building_block']);

export const repositoryResourceSchema = z.object({
  repo_origin_path: z.string().min(1),
  sources: z.array(repositorySourceSchema).nonempty(),
});

export const repositoryListResponseSchema = z.object({
  type: z.literal('list'),
  list_type: z.literal('repository'),
  has_more: z.boolean(),
  objects: z.array(repositoryResourceSchema),
});

export const harnessSchema = z.enum([
  'claudeCode',
  'codex',
  'amp',
  'lore',
  'cursor',
  'cowork',
  'unspecified',
]);

export const transcriptUploadHarnessSchema = z.enum([
  'claudeCode',
  'codex',
  'amp',
  'cursor',
  'cowork',
  'unspecified',
]);

/**
 * Cowork only — filenames discovered locally alongside the session
 * transcript (under `<session>/uploads/` and `<session>/outputs/`). The
 * server stores these on the thread_file and the cowork parser surfaces
 * them as a `system / cowork_artifacts` block prepended to the thread.
 * The actual file *contents* are NOT uploaded to Lore in v1.
 */
export const coworkArtifactMetadataSchema = z.object({
  uploads: z.array(z.string()),
  outputs: z.array(z.string()),
});

export const cliStatusResponseSchema = z.object({
  // True iff the viewer's CLI watch daemon reported a heartbeat within the
  // liveness window.
  connected: z.boolean(),
  // ISO timestamp of the most recent CLI-uploaded thread for the viewer,
  // or null if they've never uploaded one. Lets the UI show "last upload N
  // ago" without a second roundtrip.
  last_upload_at: z.string().nullable(),
  // Distinct harnesses the viewer has uploaded under. UI may use this to
  // hint which CLI command to surface (e.g., share-codex vs.
  // share-claude-code).
  harnesses: z.array(harnessSchema),
});

export const threadFileContentTypeValues = ['transcript', 'plan'] as const;
export const threadFileContentTypeSchema = z.enum(threadFileContentTypeValues);

export const skillVisibilitySchema = z.enum(['private', 'workspace', 'public']);
export const skillRootKindSchema = z.enum([
  'claude_global',
  'agents_global',
  'claude_project',
  'agents_project',
]);
export const localSkillScannerSkillSchema = z.object({
  name: z.string().min(1),
  description: z.string().nullable(),
  root_kind: skillRootKindSchema,
  file_path: z.string().min(1),
  also_seen_at: z.array(z.string().min(1)),
  skill_id: z.string().min(1).optional(),
  local_content_hash: z.string().startsWith('md5:').optional(),
});
export const coworkSkillScannerSkillSchema = z.object({
  skill_id: z.string().min(1),
  name: z.string().min(1),
  description: z.string().nullable(),
  file_path: z.string().min(1),
  manifest_path: z.string().min(1),
});
export const localSkillScannerOutputSchema = z.object({
  type: z.literal('local_skill_scanner_output'),
  skills: z.record(z.string(), z.array(localSkillScannerSkillSchema)),
  cowork_skills: z.record(z.string(), z.array(coworkSkillScannerSkillSchema)),
});
export const skillVersionStatusSchema = z.enum([
  'accepted',
  'proposed',
  'rejected',
  'superseded',
]);
export const skillSyncActionSchema = z.enum(['initial_publish', 'owner_update', 'subscriber_update']);

const skillFileRequestBaseSchema = z.object({
  md5: z.string().min(1).describe('Raw hex MD5 used to verify uploaded SKILL.md bytes'),
  name: z.string().min(1).describe('Skill directory/name, e.g. code-review'),
  filename: z.string().min(1).describe('Upload object filename, usually <name>/SKILL.md').optional(),
  description: z.string().nullable().describe('Optional skill description extracted from frontmatter').optional(),
  root_kind: skillRootKindSchema.describe('Local root kind where this skill file came from'),
  base_version: z.number().int().positive().describe('Installed base version for local edits').optional(),
});

export const skillFileRequestSchema = z.discriminatedUnion('sync_action', [
  skillFileRequestBaseSchema.extend({
    sync_action: z.literal('initial_publish').describe('Publish a local skill without an existing server skill ID'),
    skill_id: z.never().optional(),
  }),
  skillFileRequestBaseSchema.extend({
    sync_action: z.literal('owner_update').describe('Publish an accepted update to an owned skill'),
    skill_id: z.string().min(1).describe('Existing skill ID for owner updates'),
  }),
  skillFileRequestBaseSchema.extend({
    sync_action: z.literal('subscriber_update').describe('Propose an update to an installed workspace skill'),
    skill_id: z.string().min(1).describe('Existing skill ID for subscriber update proposals'),
  }),
]);

export const threadFileRequestSchema = z
  .object({
    md5: z.string().min(1),
    harness: transcriptUploadHarnessSchema,
    filename: z.string().min(1).optional(),
    repo_origin_path: z.string().min(1).optional(),
    harness_internal_id: z.string().min(1).optional(),
    // Keep this optional for older CLIs; once all supported clients send it,
    // we can make the field required in the request contract.
    file_type: threadFileContentTypeSchema.optional(),
    // Cowork only — filenames found alongside the session transcript.
    artifact_metadata: coworkArtifactMetadataSchema.optional(),
  })
  .refine(
    (threadFile) =>
      (threadFile.filename?.trim().length ?? 0) > 0 ||
      (threadFile.harness_internal_id?.trim().length ?? 0) > 0,
    {
      path: ['harness_internal_id'],
      message: 'filename or harness_internal_id is required',
    }
  );

export const threadListAuthorSchema = z.object({
  id: z.string().min(1),
  display_name: z.string().min(1),
  avatar_url: z.string().nullable(),
});

export const threadBlockCommentAuthorSchema = z.object({
  id: z.string().min(1),
  display_name: z.string().min(1),
});

export const threadBlockCommentSchema = z.object({
  id: z.string().min(1),
  author_id: z.string().min(1).nullable(),
  author: threadBlockCommentAuthorSchema.nullable(),
  created_at: z.string().datetime(),
  deleted_at: z.string().datetime().nullable(),
  content: z.string().min(1),
});

export const threadBlockCommentThreadBaseSchema = z.object({
  id: z.string().min(1),
  thread_block_id: z.string().min(1),
  created_at: z.string().datetime(),
  resolved_at: z.string().datetime().nullable(),
});

export const threadBlockCommentThreadSchema =
  threadBlockCommentThreadBaseSchema.extend({
    comments: z.array(threadBlockCommentSchema),
  });

export const threadBlockObjectSchema = z
  .object({
    id: z.string().min(1),
    type: z.string().min(1),
    index_in_thread: z.number().int().nonnegative(),
    isCritiqued: z.boolean(),
    comment_threads: z.array(threadBlockCommentThreadSchema).optional(),
  })
  .passthrough();

export const threadBlockListResponseSchema = z.object({
  type: z.literal('list'),
  list_type: z.literal('thread_block'),
  has_more: z.boolean(),
  objects: z.array(threadBlockObjectSchema),
});

export const planBlockSchema = threadBlockObjectSchema.extend({
  type: z.literal('plan'),
  prosemirror_json: prosemirrorJsonSchema,
  thread_file_id: z.string().min(1),
});

// Vote primitives. `value` is the directional vote a user can cast;
// `viewer_vote` includes 0 to encode "no vote yet from this viewer" so
// the UI can render an unfilled-arrow state without a separate field.
export const threadVoteValueSchema = z.union([z.literal(-1), z.literal(1)]);
export const viewerVoteSchema = z.union([z.literal(-1), z.literal(0), z.literal(1)]);

export const threadVoteRollupSchema = z.object({
  score: z.number().int(),
  up_count: z.number().int().nonnegative(),
  down_count: z.number().int().nonnegative(),
  viewer_vote: viewerVoteSchema,
});

export const castThreadVoteRequestSchema = z.object({
  value: threadVoteValueSchema,
});

// Top-level discussion on a public thread (Discover comments). Distinct
// from thread-block comment threads, which anchor to a specific block
// inside a transcript and behave like Slack-style resolvable threads.
export const threadCommentAuthorSchema = z.object({
  id: z.string().min(1),
  display_name: z.string().min(1),
  avatar_url: z.string().nullable(),
});

export const threadCommentSchema = z.object({
  id: z.string().min(1),
  thread_id: z.string().min(1),
  // author_id is preserved on soft-delete so reply chains can still
  // attribute "[deleted]" tombstones to the correct ancestor for
  // moderation. The matching `author` object is null only when the
  // user account itself is gone (FK ON DELETE CASCADE removes the
  // comment row entirely, so in practice this stays populated).
  author_id: z.string().min(1).nullable(),
  author: threadCommentAuthorSchema.nullable(),
  parent_comment_id: z.string().min(1).nullable(),
  // Empty when deleted_at is set; the client renders a tombstone.
  content: z.string(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
  deleted_at: z.string().datetime().nullable(),
});

export const listThreadCommentsResponseSchema = z.object({
  type: z.literal('list'),
  list_type: z.literal('thread_comment'),
  objects: z.array(threadCommentSchema),
});

export const createThreadCommentRequestSchema = z.object({
  content: z.string().trim().min(1).max(4000),
  parent_comment_id: z.string().min(1).optional(),
});

export const createThreadCommentResponseSchema = threadCommentSchema;

export const updateThreadCommentRequestSchema = z.object({
  content: z.string().trim().min(1).max(4000),
});

export const updateThreadCommentResponseSchema = threadCommentSchema;

export const deleteThreadCommentResponseSchema = threadCommentSchema;

// Realtime fan-out shape for /api/threads/:threadId/comments/stream.
// `version` is monotonic per process; clients drop events with
// `version <= lastSeenVersion` to absorb out-of-order replays. Skipping
// self-events relies on `actor_user_id`.
export const threadCommentEventSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('created'),
    thread_id: z.string().min(1),
    actor_user_id: z.string().min(1),
    version: z.number(),
    comment: threadCommentSchema,
  }),
  z.object({
    kind: z.literal('updated'),
    thread_id: z.string().min(1),
    actor_user_id: z.string().min(1),
    version: z.number(),
    comment: threadCommentSchema,
  }),
  z.object({
    kind: z.literal('deleted'),
    thread_id: z.string().min(1),
    actor_user_id: z.string().min(1),
    version: z.number(),
    comment: threadCommentSchema,
  }),
]);

export const threadSummaryStatusSchema = z.enum([
  'pending',
  'generating',
  'ready',
  'failed',
]);

export const threadTitleStatusSchema = z.enum([
  'pending',
  'generating',
  'ready',
  'failed',
]);

export const threadTitleSourceSchema = z.enum([
  'pending',
  'placeholder',
  'ai',
  'user',
  'command',
  'harness',
]);

export const decisionAnalysisStatusSchema = z.enum([
  'pending',
  'generating',
  'ready',
  'failed',
]);

export const threadCoverStatusSchema = z.enum([
  'pending',
  'generating',
  'ready',
  'failed',
  'disabled',
]);

export const threadHowToStatusSchema = z.enum([
  'pending',
  'generating',
  'ready',
  'failed',
]);

export const threadHowToStepSchema = z.object({
  title: z.string().min(1),
  body: z.string().min(1),
  files: z.array(z.string()).optional(),
});

export const threadCardCoverSchema = z.object({
  status: threadCoverStatusSchema,
  // Short-lived presigned URL the browser can render directly. Null
  // until cover_status='ready'. Resolved server-side at read time so
  // the DB stays storage-agnostic.
  url: z.string().nullable(),
  url_expires_at: z.string().nullable(),
  generated_at: z.string().nullable(),
  model: z.string().nullable(),
});

export const threadCardHowToSchema = z.object({
  status: threadHowToStatusSchema,
  steps: z.array(threadHowToStepSchema),
  generated_at: z.string().nullable(),
});

export const threadCardSchema = z.object({
  cover: threadCardCoverSchema,
  how_to: threadCardHowToSchema,
});

// Slim shape used by the landing-page card-feed scroll. Only the
// fields a card thumbnail actually renders — no transcript, no
// summary, no skills. The ThreadResource shape is too heavy for a
// 24-card horizontal strip on an unauthenticated landing.
export const cardFeedItemSchema = z.object({
  id: z.string().min(1),
  title: z.string().min(1),
  author: z.object({
    id: z.string().min(1),
    display_name: z.string(),
    handle: z.string().nullable(),
    avatar_url: z.string().nullable(),
  }),
  cover: z.object({
    url: z.string(),
    url_expires_at: z.string(),
  }),
  started_at: z.string(),
  cover_generated_at: z.string(),
});

export const cardFeedResponseSchema = z.object({
  type: z.literal('list'),
  list_type: z.literal('card_feed_item'),
  has_more: z.boolean(),
  objects: z.array(cardFeedItemSchema),
});

export const threadDecisionKindSchema = z.enum([
  'asked',
  'requested',
  'decided',
  'corrected',
  'shared',
  'requirement',
]);

export const threadDecisionResourceSchema = z.object({
  id: z.string().min(1),
  kind: threadDecisionKindSchema,
  summary: z.string().min(1),
  // ID of the user thread block this decision was extracted from. Thread
  // membership and ordering are derived through this block.
  block_id: z.string().min(1),
});

export const threadDecisionBlockStatusSchema = z.object({
  block_id: z.string().min(1),
  status: decisionAnalysisStatusSchema,
  generated_at: z.string().nullable(),
});

export const threadDecisionListResponseSchema = z.object({
  type: z.literal('list'),
  list_type: z.literal('thread_decision'),
  objects: z.array(threadDecisionResourceSchema),
  block_statuses: z.array(threadDecisionBlockStatusSchema),
});

export const forkThreadRequestSchema = z.object({
  forker_intent: z
    .string()
    .min(1)
    .max(2000)
    .describe('What the forking user wants to do next with this session'),
});

export const forkSummarySchema = z.object({
  source_thread_id: z.string().min(1),
  forker_intent: z.string(),
  source_distilled: z
    .string()
    .min(1)
    .describe('Intent-conditioned handoff summary of the source thread for a new agent'),
});

export const askThreadsRequestSchema = z.object({
  question: z
    .string()
    .trim()
    .min(1)
    .max(2000)
    .describe('Question to answer from visible Lore thread decisions and titles'),
});

export const askThreadsSourceDecisionSchema = z.object({
  id: z.string().min(1),
  kind: threadDecisionKindSchema,
  summary: z.string().min(1),
  block_id: z.string().min(1),
});

export const askThreadsSourceSchema = z.object({
  thread_id: z.string().min(1),
  title: z.string().min(1),
  summary: z.string().nullable(),
  started_at: z.string().datetime(),
  score: z.number(),
  matched_by: z.enum(['summary', 'decision', 'thread']),
  decisions: z.array(askThreadsSourceDecisionSchema),
});

export const askThreadsTraceStepSchema = z.object({
  id: z.string().min(1),
  kind: z.enum(['agent', 'tool', 'retrieval', 'answer', 'sources']),
  title: z.string().min(1),
  status: z.enum(['completed']),
  summary: z.string().min(1),
  duration_ms: z.number().nonnegative().optional(),
  metadata: z
    .object({
      retrieved_decisions: z.number().int().nonnegative().optional(),
      returned_sources: z.number().int().nonnegative().optional(),
      cited_sources: z.number().int().nonnegative().optional(),
      matched_sources: z.array(askThreadsSourceSchema).optional(),
      searched_query: z.string().min(1).optional(),
      since: z.string().min(1).optional(),
      until: z.string().min(1).optional(),
      thread_id: z.string().min(1).optional(),
      thread_ids: z.array(z.string().min(1)).optional(),
      question: z.string().min(1).optional(),
    })
    .optional(),
});

export const askThreadsTraceSchema = z.object({
  steps: z.array(askThreadsTraceStepSchema),
});

export const askThreadsResponseSchema = z.object({
  answer: z.string(),
  sources: z.array(askThreadsSourceSchema),
  matched_sources: z
    .array(askThreadsSourceSchema)
    .describe('Threads matched by the vector decision search before citation filtering'),
  searched_query: z.string().describe('String embedded for the vector decision search'),
  trace: askThreadsTraceSchema.optional(),
});

export const uploadMetadataKeyRegex = /^[a-zA-Z0-9_.:-]{1,64}$/;

export const uploadMetadataSchema = z
  .record(
    z.string().regex(
      uploadMetadataKeyRegex,
      'Metadata keys must be 1–64 characters: letters, numbers, underscore, dot, colon, or dash.',
    ),
    z.string().max(512, 'Metadata values must be at most 512 characters.'),
  )
  .refine((metadata) => Object.keys(metadata).length <= 20, {
    message: 'Upload metadata may contain at most 20 keys.',
  })
  .refine((metadata) => new TextEncoder().encode(JSON.stringify(metadata)).byteLength <= 4096, {
    message: 'Upload metadata JSON must be at most 4 KB.',
  });

export type UploadMetadata = z.infer<typeof uploadMetadataSchema>;

export const uploadApiKeyResourceSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  organization_id: z.string().min(1),
  key_prefix: z.string().min(1),
  key_last_four: z.string().length(4),
  default_visibility: z.literal('workspace'),
  created_at: z.string().datetime(),
  last_used_at: z.string().datetime().nullable(),
  revoked_at: z.string().datetime().nullable(),
});

export const uploadApiKeyListResponseSchema = z.object({
  type: z.literal('list'),
  list_type: z.literal('upload_api_key'),
  has_more: z.literal(false),
  objects: z.array(uploadApiKeyResourceSchema),
});

export const createUploadApiKeyRequestSchema = z.object({
  name: z.string().trim().min(1).max(200).describe('Human-readable Upload API key name'),
  organization_id: z
    .string()
    .min(1)
    .optional()
    .describe('Workspace organization id. Defaults to the authenticated active organization.'),
  default_visibility: z
    .literal('workspace')
    .optional()
    .describe('Default visibility for uploaded threads. Only workspace is supported in this build.'),
});

export const createUploadApiKeyResponseSchema = uploadApiKeyResourceSchema.extend({
  raw_key: z.string().min(1),
});

export const listUploadApiKeysQuerySchema = z.object({
  organization_id: z
    .string()
    .min(1)
    .optional()
    .describe('Workspace organization id. Defaults to the authenticated active organization.'),
});

export const threadResourceSchema = z.object({
  id: z.string().min(1),
  thread_file_id: z.string().min(1),
  organization_id: z.string().min(1).nullable(),
  upload_api_key_id: z.string().min(1).nullable(),
  upload_metadata: uploadMetadataSchema,
  deleted_at: z.string().datetime().nullable(),
  author: threadListAuthorSchema,
  repo: z.string().min(1),
  title: z.string().min(1),
  started_at: z.string().min(1),
  last_activity_at: z.string().datetime().nullable(),
  blocks: threadBlockListResponseSchema,
  user_message_count: z.number().int().nonnegative(),
  files_touched: z.array(z.string()),
  skills_invoked: z.array(z.string()),
  // Slash commands the *user* typed (e.g. `/share`, `/commit`). Distinct
  // from `skills_invoked`, which captures Skill tool_use the model emitted.
  slash_commands_invoked: z.array(z.string()),
  harness: harnessSchema,
  transcript_parsing_state: z.enum(['pending', 'parsed', 'too_large_to_parse', 'failed']),
  visibility: z.enum(['private', 'workspace', 'public']),
  has_plan: z.boolean().optional(),
  summary: z.string().nullable(),
  summary_status: threadSummaryStatusSchema,
  summary_generated_at: z.string().nullable(),
  title_status: threadTitleStatusSchema,
  title_generated_at: z.string().nullable(),
  title_source: threadTitleSourceSchema,
  score: z.number().int(),
  up_count: z.number().int().nonnegative(),
  down_count: z.number().int().nonnegative(),
  viewer_vote: viewerVoteSchema,
  /**
   * Live (non-soft-deleted) top-level + nested comments on this thread.
   * Always 0 for non-public threads since the comment endpoints reject
   * those, but the field is unconditionally populated so list rendering
   * doesn't need to branch.
   */
  comment_count: z.number().int().nonnegative(),
  /**
   * Whether the authenticated viewer has bookmarked this thread.
   * Always false for anonymous callers (Discover served without auth).
   */
  is_favorited: z.boolean(),
  /**
   * Lore Card facet of the thread: AI cover image and how-to recipe.
   * Always populated so list-page rendering doesn't have to branch on
   * presence; per-field statuses indicate readiness.
   */
  card: threadCardSchema,
});

export const completeThreadFileRequestSchema = z.object({
  storage_url: z.string().min(1),
});

export const completeSkillFileRequestSchema = z.object({
  storage_url: z.string().min(1),
});

export const completeThreadFileResponseSchema = z.object({
  storage_url: z.string(),
  harness: harnessSchema,
  thread: threadResourceSchema.nullable(),
});

export const completeUploadSessionResponseSchema = z.object({
  status: z.literal('complete'),
  thread_files: z.array(completeThreadFileResponseSchema),
  skill_files: z.array(z.object({
    storage_url: z.string(),
    skill: z.object({
      id: z.string(),
      name: z.string(),
      version: z.number().int().positive(),
      latest_version: z.number().int().positive(),
      visibility: skillVisibilitySchema,
      root_kind: skillRootKindSchema,
      version_status: skillVersionStatusSchema.optional(),
      content_hash: z.string(),
    }).nullable(),
  })).optional(),
});

export const threadFileResponseSchema = z.object({
  id: z.string(),
  presigned_url: z.string().nullable(),
  presigned_url_expires_at: z.string().nullable(),
  storage_url: z.string(),
  harness: harnessSchema,
  expected_md5: z.string(),
  // Server-side message explaining why no presigned_url was issued (e.g. the
  // user has already uploaded a file with this MD5). Populated only for the
  // dedup path; null for newly-issued uploads.
  error: z.string().nullable(),
  // When the create endpoint dedupes against an existing upload, the server
  // resolves the existing thread for this user and returns it here. New
  // uploads return null until the session is completed via
  // /upload_sessions/:id/complete (which is when the thread placeholder is
  // created). Letting `share`-style flows skip the upload+complete round-trip
  // when the file is already known is the whole point of this field.
  thread: threadResourceSchema.nullable(),
});

export const skillFileResponseSchema = z.object({
  id: z.string(),
  presigned_url: z.string().nullable(),
  presigned_url_expires_at: z.string().nullable(),
  storage_url: z.string(),
  expected_md5: z.string(),
  name: z.string(),
  error: z.string().nullable(),
});

export const uploadSessionResponseSchema = z.object({
  id: z.string(),
  substrate: z.enum(['s3', 'filesystem']),
  status: z.enum(['incomplete', 'locked_for_parsing', 'complete', 'error']),
  thread_files: z.array(threadFileResponseSchema),
  skill_files: z.array(skillFileResponseSchema).optional(),
});

export const resolveThreadShareHighlightRequestSchema = z.object({
  query: z.string().min(1).describe('Natural-language description of the block or block range to highlight'),
});

export const resolveThreadShareHighlightResponseSchema = z.object({
  thread_id: z.string(),
  url: z.string().url(),
  start_block_id: z.string().nullable(),
  end_block_id: z.string().nullable(),
  matched: z.boolean(),
});

export const createThreadRequestSchema = z.object({});

export const createThreadResponseSchema = z.object({
  thread_id: z.string().min(1),
});

export const updateThreadRequestSchema = z
  .object({
    visibility: z.enum(['private', 'workspace', 'public']).optional(),
    title: z.string().trim().min(1).max(200).optional(),
  })
  .refine((body) => body.visibility !== undefined || body.title !== undefined, {
    message: 'At least one of visibility or title must be provided',
  });

// Visibility-redacted thread metadata for social / OG preview consumers.
// The endpoint is unauthenticated so it's safe to fetch from crawler
// contexts — but the API must not leak thread content for private or
// workspace threads.
export const threadPreviewResponseSchema = z.discriminatedUnion('visibility', [
  z.object({
    visibility: z.literal('private'),
  }),
  z.object({
    visibility: z.literal('workspace'),
  }),
  z.object({
    visibility: z.literal('public'),
    title: z.string().min(1),
    author_display_name: z.string().min(1),
    author_avatar_url: z.string().nullable(),
    first_user_message: z.string().nullable(),
    summary: z.string().nullable(),
    started_at: z.string().min(1),
    harness: harnessSchema,
    // Presigned cover URL (null when the thread has no ready cover).
    // Consumed by the OG renderer as the social-card background.
    cover_url: z.string().nullable(),
  }),
]);

// List rows are a deliberately slim projection of a thread — only the fields
// the thread-list UI renders (see apps/web/src/lib/threadList.ts). The full
// `threadResourceSchema` (summary, card recipe, vote rollup, decisions/title
// status, etc.) is reserved for the single-thread detail endpoint; sending it
// per list item inflated every feed/profile/similar response and the client's
// persisted query cache for fields the list never reads (TAN-4243 / CL-9).
export const threadListObjectSchema = z.object({
  id: z.string().min(1),
  upload_api_key_id: z.string().min(1).nullable(),
  upload_metadata: uploadMetadataSchema,
  author: threadListAuthorSchema,
  repo: z.string().min(1),
  title: z.string().min(1),
  started_at: z.string().min(1),
  last_activity_at: z.string().datetime().nullable(),
  blocks: threadBlockListResponseSchema,
  user_message_count: z.number().int().nonnegative(),
  comment_count: z.number().int().nonnegative(),
  files_touched: z.array(z.string()),
  skills_invoked: z.array(z.string()),
  harness: harnessSchema,
  visibility: z.enum(['private', 'workspace', 'public']),
  // Optional to mirror the field on the full resource, so full thread objects
  // (e.g. the discover feed) remain structurally assignable where a list row
  // is expected when both funnel through the same client mapper.
  has_plan: z.boolean().optional(),
  is_favorited: z.boolean(),
  // The people in the thread (author + invited participants) for multiplayer
  // threads. Omitted/[] for solo threads — the UI falls back to `author`.
  participants: z
    .array(
      z.object({
        id: z.string().min(1),
        display_name: z.string(),
        avatar_url: z.string().nullable(),
      }),
    )
    .optional(),
});

export const threadListResponseSchema = z.object({
  type: z.literal('list'),
  list_type: z.literal('thread'),
  has_more: z.boolean(),
  objects: z.array(threadListObjectSchema),
});

// The Discover tab mirrors a thread resource. The server still owns the
// popularity ordering so the "Popular" sort stays stable across viewers.
export const discoverSortSchema = z.enum(['recent', 'popular']);

// 'everyone' returns all public threads; 'following' restricts to authors
// the authenticated viewer follows. 'following' requires auth and returns
// an empty list for signed-out callers or viewers who follow nobody.
export const discoverScopeSchema = z.enum(['everyone', 'following']);

export const discoverThreadResourceSchema = threadResourceSchema;

export const discoverThreadListResponseSchema = z.object({
  type: z.literal('list'),
  list_type: z.literal('discover_thread'),
  has_more: z.boolean(),
  objects: z.array(discoverThreadResourceSchema),
});

export const listDiscoverThreadsQuerySchema = z.object({
  before: z.string().min(1).optional(),
  after: z.string().min(1).optional(),
  sort: discoverSortSchema.optional(),
  scope: discoverScopeSchema.optional(),
  harness: harnessSchema.optional(),
  // 200-char cap keeps the ILIKE safe from pathological inputs and large
  // URLs from crawlers.
  search: z.string().max(200).optional(),
});

export const discoverStatsSchema = z.object({
  thread_count: z.number().int().nonnegative(),
  author_count: z.number().int().nonnegative(),
  organization_count: z.number().int().nonnegative(),
  // Map of harness → count; only harnesses with ≥1 public thread appear.
  harness_breakdown: z
    .record(z.string(), z.number().int().nonnegative())
    .refine(
      (breakdown) => Object.keys(breakdown).every((key) => harnessSchema.safeParse(key).success),
      'Harness breakdown keys must be known harness values',
    ),
});

export const uploadSessionSummarySchema = z.object({
  id: z.string(),
  status: z.enum([
    'incomplete',
    'locked_for_parsing',
    'complete',
    'error',
  ]),
  created_at: z.string(),
  thread_count: z.number().int().nonnegative(),
  // Skill uploads share the upload_sessions table; a skill-only session has
  // thread_count 0. Surfaced so notifications can label it "N skills synced"
  // instead of the misleading "0 transcripts synced". Defaulted for rollout
  // tolerance when an older API response hasn't populated it yet.
  skill_count: z.number().int().nonnegative().default(0),
  // Thread ids created from transcript uploads in this session. Used by the
  // notifications UI to deep-link directly to the uploaded thread when there
  // is exactly one transcript in the session. Defaulted for rollout tolerance
  // when an older API response has not populated it yet.
  thread_ids: z.array(z.string()).default([]),
});

export const uploadSessionListResponseSchema = z.object({
  type: z.literal('list'),
  list_type: z.literal('upload_session'),
  objects: z.array(uploadSessionSummarySchema),
});

export const threadBlockListQuerySchema = z.object({
  before: z.string().min(1).optional(),
  after: z.string().min(1).optional(),
});

export const listThreadsQuerySchema = z.object({
  before: z.string().min(1).optional(),
  after: z.string().min(1).optional(),
  author_ids: z.string().min(1).optional(),
  upload_api_key_ids: z
    .string()
    .min(1)
    .optional()
    .describe('Comma-separated Upload API key ids to filter by.'),
  upload_metadata: z
    .string()
    .min(1)
    .optional()
    .describe('URL-encoded JSON object of exact-match upload metadata filters.'),
  created_at: z.string().min(1).optional(),
  filepath_prefixes: z.string().min(1).optional(),
  // Comma-separated decision kinds (see `threadDecisionKindSchema`). Restricts
  // the feed to threads containing at least one decision of any listed kind —
  // e.g. `corrected` to surface threads where the user pushed back or fixed a
  // mistake. Unrecognized kinds are dropped; if every listed kind is unknown
  // the result is empty (the filter matched nothing), not the unfiltered feed.
  decision_kinds: z
    .string()
    .min(1)
    .optional()
    .describe(
      'Comma-separated decision kinds (asked, requested, decided, corrected, shared, requirement); returns threads with at least one matching decision.',
    ),
  q: z.string().trim().min(1).max(200).optional(),
});

export const redactThreadBlockRequestSchema = z.object({
  /**
   * Dotted JSON-ish path into the block's `content`, identifying the
   * single text field being redacted. See `apps/api/src/redactions.ts`
   * for the full set of supported paths.
   */
  path: z.string().min(1),
  start_offset: z.number().int().nonnegative(),
  end_offset: z.number().int().positive(),
});

export const skillAuthorSchema = z.object({
  id: z.string(),
  display_name: z.string(),
  avatar_url: z.string().nullable(),
});

export const listSkillsQuerySchema = z.object({
  before: z.string().min(1).optional().describe('Skill ID cursor. Returns skills newer than this cursor in descending created_at order.'),
  after: z.string().min(1).optional().describe('Skill ID cursor. Returns skills older than this cursor in descending created_at order.'),
});

export const skillSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  visibility: skillVisibilitySchema,
  root_kind: skillRootKindSchema,
  latest_version: z.number().int().nonnegative().nullable(),
  content_hash: z.string().nullable(),
  has_content: z.boolean(),
  owner: skillAuthorSchema.nullable(),
  usage_count: z.number().int().nonnegative(),
  author_count: z.number().int().nonnegative(),
  first_used_at: z.string().nullable(),
  last_used_at: z.string().nullable(),
});

export const skillIndexResponseSchema = z.object({
  type: z.literal('list'),
  list_type: z.literal('skill'),
  has_more: z.boolean(),
  objects: z.array(skillSummarySchema),
});

export const reconcileLocalSkillsQuerySchema = z.object({
  skill_ids: z.string().optional().describe('Comma-separated local Lore skill IDs, e.g. sk_...,sk_...'),
  content_hashes: z.string().optional().describe('Comma-separated local SKILL.md content hashes, e.g. md5:...,md5:...'),
});

export const localSkillReconciliationSkillSchema = skillSummarySchema.pick({
  id: true,
  name: true,
  description: true,
  visibility: true,
  root_kind: true,
  latest_version: true,
  content_hash: true,
  has_content: true,
  owner: true,
});

export const localSkillReconciliationResponseSchema = z.object({
  type: z.literal('local_skill_reconciliation'),
  objects: z.array(localSkillReconciliationSkillSchema),
});

export const skillDetailThreadSchema = z.object({
  id: z.string(),
  title: z.string(),
  visibility: z.enum(['private', 'workspace', 'public']),
  started_at: z.string(),
  author: threadListAuthorSchema,
  skills_invoked: z.array(z.string()),
});

export const skillDetailVersionSchema = z.object({
  version: z.number().int().positive().describe('Version timestamp for this SKILL.md body'),
  content_hash: z.string().describe('Content-addressed skill body hash, prefixed by hash type'),
  status: skillVersionStatusSchema.describe('Review status for this skill version'),
  created_at: z.string().describe('ISO-8601 timestamp when this version was uploaded'),
  created_by: skillAuthorSchema.nullable().describe('User who uploaded/proposed this version, when known'),
  reviewed_at: z.string().nullable().describe('ISO-8601 timestamp when the owner reviewed this version'),
  reviewed_by: skillAuthorSchema.nullable().describe('Owner who approved or rejected this version'),
  rejection_reason: z.string().nullable().describe('Owner-provided rejection reason for rejected versions'),
});

export const skillDetailResponseSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  visibility: skillVisibilitySchema,
  root_kind: skillRootKindSchema,
  latest_version: z.number().int().nonnegative().nullable(),
  content_hash: z.string().nullable(),
  owner: skillAuthorSchema.nullable(),
  usage_count: z.number().int().nonnegative(),
  author_count: z.number().int().nonnegative(),
  first_used_at: z.string().nullable(),
  last_used_at: z.string().nullable(),
  versions: z.array(skillDetailVersionSchema),
  threads: z.array(skillDetailThreadSchema),
  // Non-enumerable share slug, present only when the skill is `public`. The
  // client builds the shareable link as `${origin}/skills/s/${share_token}`.
  share_token: z.string().nullable(),
});

// Body for changing a skill's visibility (owner-only). Setting `public` mints a
// share token if the skill doesn't already have one.
export const setSkillVisibilityRequestSchema = z.object({
  visibility: skillVisibilitySchema,
});

// Public, no-login preview served for a `/skills/s/:token` share link. Enough
// to understand what the skill is; never the SKILL.md body (installing/copying
// requires a logged-in Lore account via the authenticated raw endpoint).
export const sharedSkillPreviewSchema = z.object({
  type: z.literal('shared_skill_preview'),
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  root_kind: skillRootKindSchema,
  owner: skillAuthorSchema.nullable(),
  latest_version: z.number().int().nonnegative().nullable(),
  has_content: z.boolean(),
  // True when the viewer is authenticated and may therefore install/copy the
  // skill. Anonymous viewers get a login CTA instead.
  viewer_can_install: z.boolean(),
});

// --- Artifacts ---------------------------------------------------------------
// A first-class artifact: a real file produced by a session (Cowork today,
// Lore-native multiplayer threads later), stored in object storage. Visibility
// is derived from the owning thread, so it mirrors thread visibility exactly.

export const artifactSourceSchema = z.enum(['cowork', 'native']);
export const artifactKindSchema = z.enum(['upload', 'output']);
export const artifactVisibilitySchema = z.enum(['private', 'workspace', 'public']);

export const artifactAuthorSchema = z.object({
  id: z.string(),
  display_name: z.string(),
  avatar_url: z.string().nullable(),
});

export const listArtifactsQuerySchema = z.object({
  scope: z
    .enum(['mine', 'workspace'])
    .optional()
    .describe('`mine` (default) returns artifacts you authored; `workspace` returns all visible artifacts in your org.'),
  thread_id: z
    .string()
    .optional()
    .describe('Restrict to artifacts produced by a single thread, e.g. th_...'),
  author_ids: z
    .string()
    .optional()
    .describe('Comma-separated user IDs to filter by author, e.g. user_...,user_...'),
  before: z.string().min(1).optional().describe('Artifact ID cursor. Returns artifacts newer than this cursor in descending created_at order.'),
  after: z.string().min(1).optional().describe('Artifact ID cursor. Returns artifacts older than this cursor in descending created_at order.'),
});

export const artifactSummarySchema = z.object({
  id: z.string(),
  thread_id: z.string(),
  thread_title: z.string(),
  source: artifactSourceSchema,
  kind: artifactKindSchema,
  file_name: z.string(),
  mime_type: z.string().nullable(),
  size_in_bytes: z.number().int().nonnegative().nullable(),
  content_hash: z.string().nullable(),
  visibility: artifactVisibilitySchema,
  owner: artifactAuthorSchema.nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});

export const artifactIndexResponseSchema = z.object({
  type: z.literal('list'),
  list_type: z.literal('artifact'),
  has_more: z.boolean(),
  objects: z.array(artifactSummarySchema),
});

export const artifactDetailResponseSchema = artifactSummarySchema.extend({
  download_url: z.string().describe('Presigned, time-limited URL to download the artifact bytes.'),
  download_url_expires_at: z.string().describe('ISO-8601 expiry for download_url.'),
});

// Backfill: re-upload artifact bytes for a session whose thread already exists
// (older threads uploaded before artifact-byte capture shipped). The bytes only
// exist on the user's machine, so this is CLI-driven: resolve the caller's
// thread by (harness, harness_internal_id), presign PUTs, then commit (promote
// to lore.artifacts via the shared upsert seam).
export const backfillArtifactFileSchema = z.object({
  kind: artifactKindSchema,
  file_name: z.string(),
  md5: z.string(),
  size_in_bytes: z.number().int().nonnegative(),
  content_type: z.string().nullable().optional(),
});

export const presignBackfillArtifactsRequestSchema = z.object({
  harness: z.string().describe('Harness of the source session, e.g. cowork'),
  harness_internal_id: z.string().describe('Source session id (threads.harness_internal_id)'),
  artifact_files: z.array(backfillArtifactFileSchema).min(1),
});

export const presignBackfillArtifactsResponseSchema = z.object({
  thread_id: z.string(),
  artifact_files: z.array(
    z.object({
      kind: artifactKindSchema,
      file_name: z.string(),
      presigned_url: z.string().nullable(),
      storage_url: z.string(),
    }),
  ),
});

export const commitBackfillArtifactsRequestSchema = z.object({
  harness: z.string(),
  harness_internal_id: z.string(),
  artifacts: z
    .array(
      z.object({
        kind: artifactKindSchema,
        file_name: z.string(),
        storage_url: z.string(),
        md5: z.string(),
        size_in_bytes: z.number().int().nonnegative(),
        content_type: z.string().nullable().optional(),
      }),
    )
    .min(1),
});

export const commitBackfillArtifactsResponseSchema = z.object({
  thread_id: z.string(),
  created: z.number().int().nonnegative().describe('Number of artifact rows created or updated.'),
});

export const skillInstallationSkillSchema = z.object({
  id: z.string(),
  name: z.string(),
  visibility: skillVisibilitySchema,
  root_kind: skillRootKindSchema,
  latest_version: z.number().int().nonnegative(),
  content_hash: z.string(),
});

export const skillInstallationResponseSchema = z.object({
  installation_id: z.string(),
  already_registered: z.boolean(),
  body_md_url: z.string(),
  skill: skillInstallationSkillSchema,
});

export const skillInstallationSchema = z.object({
  id: z.string(),
  skill_id: z.string(),
  installed_version: z.number().int().nonnegative().nullable(),
  already_registered: z.boolean().optional(),
});

export const updateSkillInstallationRequestSchema = z.object({
  installed_version: z
    .number()
    .int()
    .nonnegative()
    .describe('Accepted skill version that was successfully written locally'),
});

export const skillVersionMutationResponseSchema = z.object({
  id: z.string(),
  name: z.string(),
  version: z.number().int().positive(),
  latest_version: z.number().int().positive(),
  visibility: skillVisibilitySchema,
  root_kind: skillRootKindSchema,
  version_status: skillVersionStatusSchema,
  content_hash: z.string(),
});

export const unpublishSkillResponseSchema = z.object({
  id: z.string(),
  unpublished: z.literal(true),
});

export const rejectSkillVersionRequestSchema = z.object({
  reason: z.string().optional().describe('Optional owner-provided reason for rejecting the proposed version'),
});

export const skillInstallationSyncItemSchema = z.object({
  id: z.string(),
  skill_id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  root_kind: skillRootKindSchema,
  latest_version: z.number().int().nonnegative(),
  latest_content_hash: z.string(),
  installed_version: z.number().int().nonnegative().nullable(),
});

export const skillInstallationSyncResponseSchema = z.object({
  type: z.literal('list'),
  list_type: z.literal('skill_installation_sync'),
  has_more: z.boolean(),
  objects: z.array(skillInstallationSyncItemSchema),
});

export const updateThreadBlockTextRequestSchema = z.object({
  /**
   * Dotted path into the block's `content` — same allow-list the
   * redaction endpoint uses (user string content, user/assistant text
   * parts, assistant thinking parts). Writes to any other path are
   * rejected with 422.
   */
  path: z.string().min(1),
  /** Replacement text for the entire leaf at `path`. */
  text: z.string().max(64_000),
});

export const threadBlockEditKindSchema = z.enum([
  'text_edit',
  'redact',
  'delete_block',
]);

export const threadBlockEditSchema = z.object({
  id: z.string(),
  thread_block_id: z.string(),
  editor: z.object({
    id: z.string(),
    display_name: z.string(),
  }),
  kind: threadBlockEditKindSchema,
  path: z.string().nullable(),
  previous_text: z.string().nullable(),
  new_text: z.string().nullable(),
  created_at: z.string(),
});

export const threadBlockEditListResponseSchema = z.object({
  type: z.literal('list'),
  list_type: z.literal('thread_block_edit'),
  objects: z.array(threadBlockEditSchema),
});

export const adminStatsSchema = z.object({
  organization_count: z.number().int().nonnegative(),
  user_count: z.number().int().nonnegative(),
  thread_count: z.number().int().nonnegative(),
  thread_count_last_24h: z.number().int().nonnegative(),
  user_count_last_24h: z.number().int().nonnegative(),
});

export const adminOrganizationSchema = z.object({
  id: z.string(),
  name: z.string(),
  email_domains: z.array(z.string()),
  member_count: z.number().int().nonnegative(),
  thread_count: z.number().int().nonnegative(),
  default_thread_visibility: z.enum(['private', 'workspace', 'public']),
  created_at: z.string(),
});

export const adminOrganizationListResponseSchema = z.object({
  type: z.literal('list'),
  list_type: z.literal('admin_organization'),
  objects: z.array(adminOrganizationSchema),
});

export const adminUserOrganizationSchema = z.object({
  id: z.string(),
  name: z.string(),
});

export const adminUserSchema = z.object({
  id: z.string(),
  display_name: z.string(),
  workos_id: z.string(),
  thread_count: z.number().int().nonnegative(),
  onboarded_at: z.string().nullable(),
  created_at: z.string(),
  organizations: z.array(adminUserOrganizationSchema),
});

export const adminListUsersQuerySchema = z.object({
  q: z.string().trim().min(1).max(120).optional(),
});

export const adminForceFollowRequestSchema = z.object({
  follower_id: z.string().min(1),
  followee_id: z.string().min(1),
});

export const adminForceFollowResponseSchema = z.object({
  follower_id: z.string().min(1),
  followee_id: z.string().min(1),
  is_following: z.literal(true),
});

export const adminDeleteSkillResponseSchema = z.object({
  skill_id: z.string().min(1),
  deleted: z.object({
    skills: z.number().int().nonnegative(),
    versions: z.number().int().nonnegative(),
    files: z.number().int().nonnegative(),
    installations: z.number().int().nonnegative(),
  }),
  cli_command: z.string().min(1),
});

export const adminUserListResponseSchema = z.object({
  type: z.literal('list'),
  list_type: z.literal('admin_user'),
  objects: z.array(adminUserSchema),
});

export const adminThreadSchema = z.object({
  id: z.string(),
  title: z.string(),
  started_at: z.string(),
  created_at: z.string(),
  deleted_at: z.string().nullable(),
  visibility: z.enum(['private', 'workspace', 'public']),
  harness: harnessSchema,
  harness_internal_id: z.string(),
  thread_file_id: z.string(),
  transcript_parsing_state: z.enum(['pending', 'parsed', 'too_large_to_parse', 'failed']),
  author: threadListAuthorSchema,
  organization: adminUserOrganizationSchema.nullable(),
});

export const adminThreadFileSchema = z.object({
  id: z.string(),
  content_type: z.enum(['transcript', 'plan']),
  harness: harnessSchema,
  harness_internal_id: z.string(),
  expected_md5: z.string(),
  size_in_bytes: z.number().int().nonnegative().nullable(),
  created_at: z.string(),
});

export const adminThreadLookupResponseSchema = z.object({
  thread: adminThreadSchema,
  latest_transcript_file: adminThreadFileSchema.nullable(),
});

export const adminThreadListResponseSchema = z.object({
  type: z.literal('list'),
  list_type: z.literal('admin_thread'),
  has_more: z.boolean(),
  objects: z.array(adminThreadSchema),
});

export const adminListThreadsQuerySchema = z.object({
  before: z.string().min(1).optional(),
  after: z.string().min(1).optional(),
});

export const tweetLeadStatusSchema = z.enum(['new', 'replied', 'dismissed']);

export const tweetLeadSchema = z.object({
  id: z.string(),
  tweet_id: z.string(),
  author_handle: z.string(),
  author_name: z.string(),
  author_followers: z.number().int().nonnegative(),
  text: z.string(),
  url: z.string().url(),
  tweet_created_at: z.string(),
  like_count: z.number().int().nonnegative(),
  retweet_count: z.number().int().nonnegative(),
  reply_count: z.number().int().nonnegative(),
  matched_query: z.string(),
  relevance_score: z.number().int().nullable(),
  relevance_reason: z.string().nullable(),
  status: tweetLeadStatusSchema,
  collected_at: z.string(),
});

export const adminTweetLeadListResponseSchema = z.object({
  type: z.literal('list'),
  list_type: z.literal('admin_tweet_lead'),
  objects: z.array(tweetLeadSchema),
});

export const adminUpdateTweetLeadRequestSchema = z.object({
  status: tweetLeadStatusSchema,
});

export type TweetLead = z.infer<typeof tweetLeadSchema>;
export type TweetLeadStatus = z.infer<typeof tweetLeadStatusSchema>;
export type AdminTweetLeadListResponse = z.infer<typeof adminTweetLeadListResponseSchema>;
export type AdminUpdateTweetLeadRequest = z.infer<typeof adminUpdateTweetLeadRequestSchema>;

export const adminLookupThreadQuerySchema = z.object({
  thread_id: z.string().min(1).optional().describe('Lore thread id, e.g. th_xxx.'),
  thread_file_id: z.string().min(1).optional().describe('Thread file id, e.g. tf_xxx.'),
  harness: harnessSchema.optional().describe('Harness name, e.g. claudeCode.'),
  harness_internal_id: z.string().min(1).optional().describe('Harness-native session id.'),
});

export const adminReparseThreadFileResponseSchema = z.object({
  thread_id: z.string().min(1),
  thread_file_id: z.string().min(1),
  event_name: z.literal('lore/thread-files.parse.requested'),
});

export const shareTokenResponseSchema = z.object({
  token: z.string().min(1),
  thread_id: z.string().min(1),
});

export const resolveShareTokenQuerySchema = z.object({
  /**
   * Opaque client-minted visitor id used to dedupe view counts. Not a
   * fingerprint — a random UUID the browser stashes in localStorage so
   * multiple refreshes of the same share link count as one unique view.
   */
  visitor: z.string().min(1).max(64).optional(),
});

export const adminGrowthTopSharerSchema = z.object({
  user_id: z.string(),
  display_name: z.string(),
  shares_sent: z.number().int().nonnegative(),
  unique_viewers: z.number().int().nonnegative(),
  attributed_signups: z.number().int().nonnegative(),
});

export const adminGrowthStatsSchema = z.object({
  window_days: z.number().int().positive(),
  shares_sent: z.number().int().nonnegative(),
  unique_viewers: z.number().int().nonnegative(),
  attributed_signups: z.number().int().nonnegative(),
  active_sharers: z.number().int().nonnegative(),
  k_factor: z.number().nullable(),
  shares_sent_sparkline: z.array(z.number().int().nonnegative()),
  top_sharers: z.array(adminGrowthTopSharerSchema),
});

export const adminGrowthResponseSchema = z.object({
  windows: z.array(adminGrowthStatsSchema),
});

export const adminActiveUsersBucketSchema = z.object({
  period: z.string(),
  active: z.number().int().nonnegative(),
});

export const adminActiveUsersResponseSchema = z.object({
  daily: z.array(adminActiveUsersBucketSchema),
  weekly: z.array(adminActiveUsersBucketSchema),
  monthly: z.array(adminActiveUsersBucketSchema),
  allTime: z.array(adminActiveUsersBucketSchema),
});

export const critiqueAnnotationSchema = z.object({
  quoted_text: z.string().min(1),
  comment: z.string().nullable(),
  type: z.enum(['comment', 'highlight:trapdoor']),
});

export const critiqueCommentThreadSchema = threadBlockCommentThreadBaseSchema;

export const critiqueCommentSchema = threadBlockCommentSchema.extend({
  comment_thread_id: z.string().min(1),
});

export const adminCritiquePlaygroundRunRequestSchema = z.object({
  markdown: z.string().min(1),
});

export const adminCritiquePlaygroundRunResponseSchema = z.object({
  storage_url: z.string().min(1),
  thread_file_id: z.string().min(1),
  parsed_plan_block: planBlockSchema,
  critiqued_plan_block: planBlockSchema,
  annotations: z.array(critiqueAnnotationSchema),
  comment_threads: z.array(critiqueCommentThreadSchema),
  comments: z.array(critiqueCommentSchema),
});

export const emailTemplateFieldSchema = z.object({
  key: z.string().min(1),
  label: z.string().min(1),
  description: z.string().optional(),
  type: z.enum(['text', 'textarea', 'url', 'email']),
  required: z.boolean(),
  defaultValue: z.string(),
});

export const emailTemplateSummarySchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  description: z.string().min(1),
  deliveryTrigger: z.string().min(1),
  fields: z.array(emailTemplateFieldSchema),
  availableFormats: z.array(z.enum(['html', 'text'])),
});

export const adminEmailTemplateListResponseSchema = z.object({
  templates: z.array(emailTemplateSummarySchema),
});

export const adminEmailTemplatePreviewRequestSchema = z.object({
  values: z.record(z.string(), z.string()),
});

export const renderedEmailSchema = z.object({
  subject: z.string().min(1),
  html: z.string().optional(),
  text: z.string().optional(),
});

export const adminEmailTemplateSendRequestSchema = z.object({
  to: z.string().email().describe('Recipient email address.'),
  values: z.record(z.string(), z.string()),
});

export const adminEmailTemplateSendResponseSchema = z.object({
  id: z.string().min(1),
});

// ------- Billing -------

export const planTierSchema = z.enum(PLAN_TIERS);

export const billingFeaturesSchema = z.object({
  review: z.boolean(),
});

export const billingSubjectSummarySchema = z.object({
  subject_type: z.enum(['user', 'organization']),
  subject_id: z.string().min(1),
  effective_plan: planTierSchema,
  admin_override: planTierSchema.nullable(),
  stripe_subscription_status: z.string().nullable(),
  cancel_at_period_end: z.boolean(),
  current_period_end: z.string().nullable(),
  seat_quantity: z.number().int().positive(),
  /**
   * Total member count for org subjects (null for user subjects).
   * Clients compare against `seat_quantity` to surface a
   * "you have more people than seats" banner; we don't gate access
   * on the overage — Team entitlements stay org-wide.
   */
  member_count: z.number().int().nonnegative().nullable(),
  features: billingFeaturesSchema,
});

export const billingStateResponseSchema = z.object({
  /**
   * The caller's resolved plan — max of user-level and org-level
   * tiers after admin overrides apply. Single source of truth for
   * "what plan is the viewer on"; clients should prefer this over
   * inspecting user/organization summaries individually.
   */
  effective_plan: planTierSchema,
  features: billingFeaturesSchema,
  user: billingSubjectSummarySchema,
  organization: billingSubjectSummarySchema.nullable(),
  /**
   * Whether the caller is a billing admin of their org. Clients use this to
   * show vs hide payment-sensitive controls. Always false with no org.
   */
  is_billing_admin: z.boolean(),
});

export const createCheckoutSessionRequestSchema = z.object({
  plan: z.enum(['creator', 'team']),
  /** Requested seat quantity for Team. Ignored for Creator. */
  seat_quantity: z.number().int().positive().optional(),
});

export const createCheckoutSessionResponseSchema = z.object({
  checkout_url: z.string().min(1),
});

export const createBillingPortalResponseSchema = z.object({
  portal_url: z.string().min(1),
});

export const updateTeamSeatsRequestSchema = z.object({
  seat_quantity: z.number().int().positive(),
});

export const adminPlanOverrideRequestSchema = z.object({
  /** Null clears the override and returns the subject to whatever
   * Stripe says; otherwise forces the subject onto the given tier. */
  plan: planTierSchema.nullable(),
});

export const adminBillingSubjectSchema = z.object({
  subject_type: z.enum(['user', 'organization']),
  subject_id: z.string().min(1),
  label: z.string().min(1),
  effective_plan: planTierSchema,
  admin_override: planTierSchema.nullable(),
  stripe_subscription_status: z.string().nullable(),
  seat_quantity: z.number().int().positive(),
});

export const adminBillingOverviewResponseSchema = z.object({
  users: z.array(adminBillingSubjectSchema),
  organizations: z.array(adminBillingSubjectSchema),
});

// ------- Team credit pool + auto-recharge -------
// Amounts are in millicents (1 US cent = 1000 millicents) to match the
// server-side ledger; clients format to dollars for display.

export const creditSettingsResponseSchema = z.object({
  /** Current pooled balance, in millicents. */
  pool_balance_millicents: z.number().int() /* balance may be negative (overspend) */,
  /** End of the current credit period (ISO), or null if no grant yet. */
  period_end: z.string().nullable(),
  auto_recharge_enabled: z.boolean(),
  trigger_threshold_millicents: z.number().int().nonnegative(),
  topup_amount_millicents: z.number().int().positive().nullable(),
});

export const updateCreditSettingsRequestSchema = z.object({
  auto_recharge_enabled: z.boolean().optional(),
  trigger_threshold_millicents: z.number().int().nonnegative().optional(),
  /**
   * null clears the chosen top-up amount; required before enabling.
   * Minimum $20 (MIN_TOPUP_MILLICENTS) so auto-recharge clears Stripe's
   * card-charge floor and stays meaningful against per-charge fees.
   */
  topup_amount_millicents: z
    .number()
    .int()
    .min(MIN_TOPUP_MILLICENTS)
    .nullable()
    .optional(),
});

export const createCreditTopUpRequestSchema = z.object({
  /** Manual one-off top-up amount, in millicents. Minimum $20. */
  amount_millicents: z.number().int().min(MIN_TOPUP_MILLICENTS),
  /**
   * Client-supplied idempotency key (e.g. a UUID per top-up action) so a
   * retry or double-submit doesn't double-charge the card. Optional, but
   * clients should send a stable key per user action.
   */
  idempotency_key: z.string().min(1).max(255).optional(),
});

export const createCreditTopUpResponseSchema = z.object({
  pool_balance_millicents: z.number().int() /* balance may be negative (overspend) */,
});

// ------- Credit pool instrumentation (admin) -------

export const creditPoolMetricSchema = z.object({
  subject_id: z.string().min(1),
  label: z.string(),
  current_balance_millicents: z.number().int() /* balance may be negative (overspend) */,
  period_start: z.string().nullable(),
  period_end: z.string().nullable(),
  granted_millicents: z.number().int().nonnegative(),
  used_millicents: z.number().int().nonnegative(),
  raw_cogs_millicents: z.number().int().nonnegative(),
  breakage_millicents: z.number().int().nonnegative(),
  overage_millicents: z.number().int().nonnegative(),
  utilization_pct: z.number().nullable(),
  margin_pct: z.number().nullable(),
});

export const adminCreditPoolsResponseSchema = z.object({
  pools: z.array(creditPoolMetricSchema),
  rollup: z.object({
    team_count: z.number().int().nonnegative(),
    granted_millicents: z.number().int().nonnegative(),
    used_millicents: z.number().int().nonnegative(),
    raw_cogs_millicents: z.number().int().nonnegative(),
    breakage_millicents: z.number().int().nonnegative(),
    overage_millicents: z.number().int().nonnegative(),
    blended_margin_pct: z.number().nullable(),
    avg_utilization_pct: z.number().nullable(),
  }),
});

export const liveThreadAuthorSchema = z.object({
  id: z.string().min(1),
  display_name: z.string().min(1),
  /** Public-facing handle, null until the user picks one. */
  handle: z.string().nullable(),
  /**
   * Resolved avatar URL (presigned download or null) — same source of
   * truth as the user's profile page and the AppHeader avatar, so the
   * Live Now rail never shows a different photo than the rest of the
   * app.
   */
  avatar_url: z.string().nullable(),
});

export const liveThreadObjectSchema = z.object({
  thread_id: z.string().min(1),
  title: z.string().min(1),
  author: liveThreadAuthorSchema,
  last_active_at: z.string().datetime(),
  /**
   * One-line plain-text excerpt of the most recent user block in this live
   * thread — what the author is actually doing right now. Null when the block
   * content has no extractable text.
   */
  last_block_excerpt: z.string().nullable(),
  /** Block type of the excerpt source — useful for the UI to label it. */
  last_block_kind: z.string().min(1).nullable(),
  /** Retained for API compatibility; live activity is based on user blocks. */
  is_streaming: z.boolean(),
});

export const liveThreadListResponseSchema = z.object({
  type: z.literal('list'),
  list_type: z.literal('live_thread'),
  objects: z.array(liveThreadObjectSchema),
});

export const recordHeartbeatRequestSchema = z.object({
  thread_id: z.string().min(1),
});

export const recordHeartbeatResponseSchema = z.object({
  thread_id: z.string().min(1),
  last_seen_at: z.string().datetime(),
  started_at: z.string().datetime(),
});

export const recordProductPresenceResponseSchema = z.object({
  user_id: z.string().min(1),
  organization_id: z.string().min(1).nullable(),
  last_seen_at: z.string().datetime(),
  started_at: z.string().datetime(),
});

export const recordCliHeartbeatRequestSchema = z.object({
  upload_completed: z
    .boolean()
    .optional()
    .describe('When true, also bump last_upload_at — the daemon completed an upload pass.'),
});

export const recordCliHeartbeatResponseSchema = z.object({
  last_seen_at: z.string().datetime(),
  last_upload_at: z.string().datetime().nullable(),
  started_at: z.string().datetime(),
});

// ---------- Profile: follows ----------

export const followUserResponseSchema = z.object({
  follower_id: z.string().min(1),
  followee_id: z.string().min(1),
  is_following: z.literal(true),
  follower_count: z.number().int().nonnegative(),
});

export const unfollowUserResponseSchema = z.object({
  follower_id: z.string().min(1),
  followee_id: z.string().min(1),
  is_following: z.literal(false),
  follower_count: z.number().int().nonnegative(),
});

export const profileMiniUserSchema = z.object({
  id: z.string().min(1),
  display_name: z.string().min(1),
  handle: z.string().nullable(),
  avatar_url: z.string().nullable(),
  bio: z.string().max(280).nullable(),
  is_following: z.boolean(),
  is_self: z.boolean(),
});

export const userFollowListResponseSchema = z.object({
  type: z.literal('list'),
  list_type: z.literal('user_follow'),
  objects: z.array(profileMiniUserSchema),
});

export const followSuggestionSchema = profileMiniUserSchema.extend({
  /** Viewer's followees who also follow this candidate. */
  mutuals: z.array(profileMiniUserSchema),
  mutual_count: z.number().int().nonnegative(),
});

export const followSuggestionListResponseSchema = z.object({
  type: z.literal('list'),
  list_type: z.literal('follow_suggestion'),
  objects: z.array(followSuggestionSchema),
});

// ---------- Profile: invite-link referrals ----------

export const referralRefereeSchema = profileMiniUserSchema.extend({
  /**
   * ISO timestamp when the inviter-link attribution was written.
   * Matches the referee's `created_at` because attribution lands on
   * their first post-signup whoami.
   */
  referred_at: z.string().min(1),
});

export const referralListResponseSchema = z.object({
  type: z.literal('list'),
  list_type: z.literal('referral'),
  /** Total people the viewer has referred — not just the truncated recent list. */
  count: z.number().int().nonnegative(),
  /** Most-recent referees first; capped server-side so the response stays small. */
  recent: z.array(referralRefereeSchema),
});

// ---------- Admin: cross-org referral analytics ----------

export const adminReferralsDailyPointSchema = z.object({
  /** YYYY-MM-DD in UTC. */
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  /**
   * Users whose `referred_by_user_id` was set on this date — i.e. invite-link
   * signups attributed on that day. Always less than or equal to `total`.
   */
  referred: z.number().int().nonnegative(),
  /** All new users created that day, regardless of attribution source. */
  total: z.number().int().nonnegative(),
});

export const adminReferralsTopInviterSchema = z.object({
  inviter: profileMiniUserSchema,
  /**
   * Display name of the first WorkOS organization the inviter belongs
   * to, surfaced inline on the admin leaderboard so we can tell which
   * org a power inviter is bringing people into. Null when the inviter
   * has no WorkOS memberships or the lookup failed.
   */
  organization_name: z.string().nullable(),
  /** How many users the inviter has attributed via their invite link. */
  referred_count: z.number().int().positive(),
  /** ISO timestamp of the most-recent attribution for this inviter. */
  latest_referred_at: z.string().min(1),
});

export const adminReferralsAttributionSchema = z.object({
  inviter: profileMiniUserSchema,
  referee: profileMiniUserSchema,
  /** When the attribution row was written (referee's `created_at`). */
  attributed_at: z.string().min(1),
});

export const adminReferralsAnalyticsResponseSchema = z.object({
  /** Lifetime count of users whose `referred_by_user_id` is set. */
  total_referred_signups: z.number().int().nonnegative(),
  /** Distinct inviters whose link has ever attributed a signup. */
  active_inviters: z.number().int().nonnegative(),
  /** Invite-link signups in the trailing 30 days. */
  referred_signups_last_30d: z.number().int().nonnegative(),
  /** All new users in the trailing 30 days, regardless of attribution. */
  total_signups_last_30d: z.number().int().nonnegative(),
  /** Trailing 60 days of daily signup totals, oldest first. */
  daily_counts: z.array(adminReferralsDailyPointSchema),
  /** Top inviters by `referred_count`, ranked descending. */
  top_inviters: z.array(adminReferralsTopInviterSchema),
  /** Most-recent attributions, newest first. */
  recent_attributions: z.array(adminReferralsAttributionSchema),
});

// ---------- Admin: onboarding flow (PostHog-sourced) ----------

export const adminOnboardingSourceRowSchema = z.object({
  /** First-touch source identifier (e.g. "linkedin", "google", "direct"). */
  source: z.string().min(1),
  /** Count of signed-up persons attributed to this source. */
  signups: z.number().int().nonnegative(),
});

export const adminOnboardingSourcesResponseSchema = z.object({
  /** Sources, ranked by signup count descending. */
  sources: z.array(adminOnboardingSourceRowSchema),
  /** Signed-up persons with `initial_source` set. */
  total_with_source: z.number().int().nonnegative(),
  /** Signed-up persons with no `initial_source` (CLI-only, etc.). */
  total_without_source: z.number().int().nonnegative(),
});

// ---------- Profile: contributions heatmap ----------

export const userContributionDaySchema = z.object({
  /** YYYY-MM-DD in UTC. */
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  count: z.number().int().nonnegative(),
});

export const userContributionsResponseSchema = z.object({
  /** ISO date (UTC, midnight) of the first day in the window — inclusive. */
  window_start: z.string().datetime(),
  /** ISO date (UTC, midnight) of the last day in the window — inclusive. */
  window_end: z.string().datetime(),
  total_contributions: z.number().int().nonnegative(),
  days_active: z.number().int().nonnegative(),
  /** Average contributions per day across the entire window (active or not). */
  daily_average: z.number().nonnegative(),
  /**
   * Length of the user's current consecutive-day activity streak ending
   * at `window_end`. Zero if there's no activity on `window_end`.
   */
  current_streak_days: z.number().int().nonnegative(),
  /** Dense array — every day in the window, count zero-filled. */
  days: z.array(userContributionDaySchema),
});

// ---------- Profile: activity feed ----------

export const userActivityThreadCreatedSchema = z.object({
  type: z.literal('thread_created'),
  occurred_at: z.string().datetime(),
  /** Stable id for pagination — unique per (type, source row). */
  id: z.string().min(1),
  thread: z.object({
    id: z.string().min(1),
    title: z.string(),
    visibility: z.enum(['private', 'workspace', 'public']),
  }),
});

/**
 * A thread the user already shared that gained new activity afterwards
 * (e.g. a re-uploaded / growing session). Distinct from `thread_created`
 * so the feed can read "Updated a thread" instead of mislabeling the
 * re-upload as a brand-new thread. Same payload shape as
 * `thread_created`; the API decides the variant by comparing the
 * canonical `lastActivityAt` against the row's `createdAt`.
 */
export const userActivityThreadUpdatedSchema = z.object({
  type: z.literal('thread_updated'),
  occurred_at: z.string().datetime(),
  /** Stable id for pagination — unique per (type, source row). */
  id: z.string().min(1),
  thread: z.object({
    id: z.string().min(1),
    title: z.string(),
    visibility: z.enum(['private', 'workspace', 'public']),
  }),
});

export const userActivityCommentPostedSchema = z.object({
  type: z.literal('comment_posted'),
  occurred_at: z.string().datetime(),
  id: z.string().min(1),
  comment_excerpt: z.string(),
  thread: z.object({
    id: z.string().min(1),
    title: z.string(),
    visibility: z.enum(['private', 'workspace', 'public']),
  }),
});

export const userActivityProjectPublishedSchema = z.object({
  type: z.literal('project_published'),
  occurred_at: z.string().datetime(),
  id: z.string().min(1),
  project: z.object({
    id: z.string().min(1),
    name: z.string().min(1),
  }),
});

export const userActivityFollowedSchema = z.object({
  type: z.literal('user_followed'),
  occurred_at: z.string().datetime(),
  id: z.string().min(1),
  followee: profileMiniUserSchema,
});

export const userActivityEventSchema = z.discriminatedUnion('type', [
  userActivityThreadCreatedSchema,
  userActivityThreadUpdatedSchema,
  userActivityCommentPostedSchema,
  userActivityProjectPublishedSchema,
  userActivityFollowedSchema,
]);

export const userActivityListResponseSchema = z.object({
  type: z.literal('list'),
  list_type: z.literal('user_activity'),
  objects: z.array(userActivityEventSchema),
});

// ---------- Profile: published projects ----------

export const profilePublishedProjectSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  /** Plain-text excerpt of the project body, truncated for cards. */
  excerpt: z.string(),
  published_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});

export const profilePublishedProjectListResponseSchema = z.object({
  type: z.literal('list'),
  list_type: z.literal('profile_published_project'),
  objects: z.array(profilePublishedProjectSchema),
});

export const publishProjectResponseSchema = z.object({
  id: z.string().min(1),
  published_at: z.string().datetime().nullable(),
});

// ---------- Profile: avatar/banner upload ----------

export const profileImageKindSchema = z.enum(['avatar', 'banner']);
export const profileImageContentTypeSchema = z.enum([
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
]);

/**
 * Profile-image upload payload. The client base64-encodes the bytes
 * and posts them with `kind` + `content_type`; the API streams to its
 * configured storage substrate using its own credentials and returns
 * the canonical storage URL.
 *
 * We chose JSON+base64 over multipart or browser-direct presigned PUTs
 * because the latter requires per-bucket CORS configuration that
 * doesn't exist on the production thread-files bucket. Streaming
 * through the API keeps dev (minio) and prod (S3) on the same code
 * path. 5 MB binary → ~6.7 MB base64 fits inside the 30 MB Fastify
 * body limit configured in apps/api/src/index.ts.
 */
export const profileImageUploadRequestSchema = z.object({
  kind: profileImageKindSchema,
  content_type: profileImageContentTypeSchema,
  /** Base64-encoded image bytes (no `data:` prefix). */
  base64_data: z.string().min(1),
});

export const profileImageUploadResponseSchema = z.object({
  /** Substrate the bytes ended up on — clients only need this for telemetry. */
  substrate: z.enum(['s3', 'filesystem']),
  /** Storage URL to send back to PATCH /users/me to commit the change. */
  storage_url: z.string().min(1),
});

/**
 * Thread-cover upload payload. Same JSON+base64 shape the profile
 * upload uses, scoped to the thread-cover bucket key. The handler
 * commits the new cover in one call (storage write + threads row
 * update) so the cover-row state is never half-applied — no separate
 * PATCH like profile-image needs.
 */
export const threadCoverUploadRequestSchema = z.object({
  content_type: profileImageContentTypeSchema,
  /** Base64-encoded image bytes (no `data:` prefix). */
  base64_data: z.string().min(1),
});

export const threadCoverUploadResponseSchema = z.object({
  cover_status: threadCoverStatusSchema,
  /** Short-lived presigned URL the client can render immediately. */
  cover_url: z.string().min(1),
  cover_url_expires_at: z.string().min(1),
  cover_generated_at: z.string().min(1),
});

// ---------- Profile: by-handle resolution ----------

export const profileByHandleResponseSchema = userProfileResourceSchema;

export const apiContract = c.router(
  {
    health: {
      method: 'GET',
      path: '/health',
      responses: {
        200: z.object({
          status: z.literal('ok'),
        }),
      },
      summary: 'Health check',
    },
    getDeepHealth: {
      method: 'GET',
      path: '/health/deep',
      responses: {
        200: z.object({
          status: z.enum(['ok', 'degraded']),
          environment: z.string(),
          checks: z.object({
            database: z.object({
              ok: z.boolean(),
              latencyMs: z.number().int().nonnegative().optional(),
              error: z.string().optional(),
            }),
            migrations: z.object({
              ok: z.boolean(),
              appliedCount: z.number().int().nonnegative(),
              pendingCount: z.number().int().nonnegative(),
              latestApplied: z.string().optional(),
              pending: z.array(z.string()),
            }),
            storage: z.object({
              ok: z.boolean(),
              bucket: z.string().optional(),
              error: z.string().optional(),
            }),
          }),
        }),
        503: z.object({
          status: z.literal('down'),
          environment: z.string(),
          error: z.string(),
        }),
      },
      summary:
        'Diagnostic health check covering database connectivity, migration drift, and storage reachability. Used by smoke tests and operators; NOT wired into Railway healthcheckPath.',
    },
    whoAmI: {
      method: 'GET',
      path: '/whoami',
      headers: z.object({
        authorization: z.string().min(1).optional(),
        // When present on the first post-signup whoami, the server
        // credits a share-link referral to the sharer (best-effort).
        'x-lore-referral-token': z.string().min(1).max(16).optional(),
        // Inviter user id captured from `/?invited_by=<handle-or-id>`
        // landings. On the first post-signup whoami, the server
        // writes it to users.referred_by_user_id (best-effort).
        'x-lore-invited-by-user-id': z.string().min(1).max(40).optional(),
        // Standard browser/CLI User-Agent. Declared in the contract so
        // ts-rest's header validator doesn't strip it before the
        // handler runs — the handler reads it to detect CLI requests
        // (User-Agent prefix `@loredotlink/cli/`, or legacy `@tanagram/lore/`)
        // and fire `cli.activated`.
        'user-agent': z.string().min(1).max(1024).optional(),
      }),
      responses: {
        200: whoAmIResponseSchema,
        401: errorSchema,
        500: errorSchema,
        502: errorSchema,
      },
      summary: 'Validate a WorkOS Bearer token and return member plus user fields',
    },
    getCliAuthConfig: {
      method: 'GET',
      path: '/cli-auth/config',
      responses: {
        200: cliAuthConfigResponseSchema,
        503: errorSchema,
      },
      summary: 'Return public WorkOS CLI Auth configuration for the Lore CLI',
    },
    getDesktopAuthConfig: {
      method: 'GET',
      path: '/desktop-auth/config',
      responses: {
        200: cliAuthConfigResponseSchema,
        503: errorSchema,
      },
      summary:
        'Return public WorkOS configuration for the Lore desktop app (dedicated native client, separate from the web/CLI client)',
    },
    getCliStatus: {
      method: 'GET',
      path: '/users/me/cli-status',
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      responses: {
        200: cliStatusResponseSchema,
        401: errorSchema,
      },
      summary:
        'Return whether the viewer has connected the Lore CLI (i.e., uploaded any thread under a non-unspecified harness), plus the latest upload timestamp.',
    },
    recordHeartbeat: {
      method: 'POST',
      path: '/heartbeat',
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      body: recordHeartbeatRequestSchema,
      responses: {
        200: recordHeartbeatResponseSchema,
        401: errorSchema,
        403: errorSchema,
        404: errorSchema,
        422: errorSchema,
      },
      summary:
        'Record (or refresh) a presence heartbeat for the authenticated user against one of their own threads. Idempotent; the daemon should call this every ~30s while a session is open.',
    },
    recordProductPresence: {
      method: 'POST',
      path: '/users/me/presence',
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      body: z.object({}).optional(),
      responses: {
        200: recordProductPresenceResponseSchema,
        401: errorSchema,
      },
      summary:
        'Record (or refresh) an authenticated Lore web-app presence heartbeat for the current user. Browser clients call this while the product is open.',
    },
    recordCliHeartbeat: {
      method: 'POST',
      path: '/users/me/cli-heartbeat',
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      body: recordCliHeartbeatRequestSchema,
      responses: {
        200: recordCliHeartbeatResponseSchema,
        401: errorSchema,
      },
      summary:
        "Record a liveness heartbeat for the authenticated user's CLI watch daemon. Idempotent; the daemon calls this ~every 60s and on each completed upload (upload_completed:true). Daemon-internal — not a `lore` subcommand.",
    },
    listLiveThreads: {
      method: 'GET',
      path: '/threads/live',
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      responses: {
        200: liveThreadListResponseSchema,
        401: errorSchema,
        403: errorSchema,
      },
      summary:
        'List people with user-block activity in the last 10 minutes. Includes the viewer’s workspace and followed authors, deduped per author, max 10.',
    },
    listThreads: {
      method: 'GET',
      path: '/threads',
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      query: listThreadsQuerySchema,
      responses: {
        200: threadListResponseSchema,
        401: errorSchema,
      },
      summary: 'List threads visible to the authenticated user',
    },
    createThread: {
      method: 'POST',
      path: '/threads',
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      body: createThreadRequestSchema,
      responses: {
        201: createThreadResponseSchema,
        401: errorSchema,
      },
      summary: 'Create an empty Lore-native thread for the authenticated user',
    },
    getThread: {
      method: 'GET',
      path: '/threads/:id',
      pathParams: z.object({
        id: z.string().min(1),
      }),
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      responses: {
        200: threadResourceSchema,
        404: errorSchema,
      },
      summary: 'Load a visible thread by id',
    },
    getSimilarThreads: {
      method: 'GET',
      path: '/threads/:id/similar',
      pathParams: z.object({
        id: z.string().min(1),
      }),
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      responses: {
        200: threadListResponseSchema,
        404: errorSchema,
        409: errorSchema,
      },
      summary: 'List the top 20 visible threads with the closest summary embedding',
    },
    resolveThreadShareHighlight: {
      method: 'POST',
      path: '/threads/:id/share-highlight',
      pathParams: z.object({
        id: z.string().min(1).describe('Thread id to share'),
      }),
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      body: resolveThreadShareHighlightRequestSchema,
      responses: {
        200: resolveThreadShareHighlightResponseSchema,
        401: errorSchema,
        404: errorSchema,
        409: errorSchema,
      },
      summary: 'Resolve a natural-language share highlight to a canonical /thread URL with block anchors',
    },
    askThreads: {
      method: 'POST',
      path: '/threads/ask',
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      body: askThreadsRequestSchema,
      responses: {
        200: askThreadsResponseSchema,
        401: errorSchema,
      },
      summary: 'Answer a question from the top 20 visible decision matches, grouped back into threads',
    },
    forkThread: {
      method: 'POST',
      path: '/threads/:id/fork',
      pathParams: z.object({
        id: z.string().min(1).describe('Thread id or stable harness lookup id to fork'),
      }),
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      body: forkThreadRequestSchema,
      responses: {
        200: forkSummarySchema,
        403: errorSchema,
        404: errorSchema,
      },
      summary: 'Generate a distilled source handoff for continuing a visible coding-assistant session',
    },
    listThreadBlocks: {
      method: 'GET',
      path: '/threads/:id/blocks',
      pathParams: z.object({
        id: z.string().min(1),
      }),
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      query: threadBlockListQuerySchema,
      responses: {
        200: threadBlockListResponseSchema,
        401: errorSchema,
      },
      summary: 'List blocks for a visible thread',
    },
    createThreadBlockCommentThread: {
      method: 'POST',
      path: '/threads/:threadId/blocks/:blockId/comment-threads',
      pathParams: z.object({
        threadId: z.string().min(1),
        blockId: z.string().min(1),
      }),
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      body: createThreadBlockCommentThreadRequestSchema,
      responses: {
        201: createThreadBlockCommentThreadResponseSchema,
        401: errorSchema,
        404: errorSchema,
      },
      summary: 'Create a new block-level comment thread on a visible thread block',
    },
    createThreadBlockComment: {
      method: 'POST',
      path: '/threads/:threadId/blocks/:blockId/comment-threads/:commentThreadId/comments',
      pathParams: z.object({
        threadId: z.string().min(1),
        blockId: z.string().min(1),
        commentThreadId: z.string().min(1),
      }),
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      body: createThreadBlockCommentRequestSchema,
      responses: {
        201: createThreadBlockCommentResponseSchema,
        401: errorSchema,
        404: errorSchema,
        409: errorSchema,
      },
      summary: 'Reply to a block-level comment thread on a visible thread block',
    },
    updateThreadBlockCommentThread: {
      method: 'PATCH',
      path: '/threads/:threadId/blocks/:blockId/comment-threads/:commentThreadId',
      pathParams: z.object({
        threadId: z.string().min(1),
        blockId: z.string().min(1),
        commentThreadId: z.string().min(1),
      }),
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      body: updateThreadBlockCommentThreadRequestSchema,
      responses: {
        200: updateThreadBlockCommentThreadResponseSchema,
        401: errorSchema,
        404: errorSchema,
      },
      summary: 'Resolve or reopen a block-level comment thread on a visible thread block',
    },
    listSkills: {
      method: 'GET',
      path: '/skills',
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      query: listSkillsQuerySchema,
      responses: {
        200: skillIndexResponseSchema,
        401: errorSchema,
      },
      summary: 'List visible workspace skills',
    },
    reconcileLocalSkills: {
      method: 'GET',
      path: '/skills/reconcile-local',
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      query: reconcileLocalSkillsQuerySchema,
      responses: {
        200: localSkillReconciliationResponseSchema,
        401: errorSchema,
      },
      summary: 'Reconcile scanned local skills with visible server-side skills by ID or content hash',
    },
    getSkillDetail: {
      method: 'GET',
      path: '/skills/:id',
      pathParams: z.object({
        id: z.string().min(1).describe('Stable skill ID, e.g. sk_...'),
      }),
      query: z.object({
        include_unpublished: z.coerce.boolean().optional().describe('Allow the owner to load a soft-unpublished skill for republishing.'),
      }),
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      responses: {
        200: skillDetailResponseSchema,
        401: errorSchema,
        404: errorSchema,
      },
      summary: 'Get visible skill details by stable skill ID',
    },
    getSharedSkill: {
      method: 'GET',
      path: '/skills/share/:token',
      pathParams: z.object({
        token: z.string().min(1).describe('Public share slug from a /skills/s/:token link.'),
      }),
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      responses: {
        200: sharedSkillPreviewSchema,
        404: errorSchema,
      },
      summary: 'Public no-login preview of a skill shared by link (install requires auth)',
    },
    setSkillVisibility: {
      method: 'PATCH',
      path: '/skills/:id/visibility',
      pathParams: z.object({
        id: z.string().min(1).describe('Stable skill ID, e.g. sk_...'),
      }),
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      body: setSkillVisibilityRequestSchema,
      responses: {
        200: skillDetailResponseSchema,
        401: errorSchema,
        403: errorSchema,
        404: errorSchema,
      },
      summary: 'Set a skill\'s visibility (owner only); minting a share link when set to public',
    },
    listArtifacts: {
      method: 'GET',
      path: '/artifacts',
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      query: listArtifactsQuerySchema,
      responses: {
        200: artifactIndexResponseSchema,
        401: errorSchema,
      },
      summary: 'List visible artifacts (files produced by Cowork or native threads)',
    },
    getArtifactDetail: {
      method: 'GET',
      path: '/artifacts/:id',
      pathParams: z.object({
        id: z.string().min(1).describe('Stable artifact ID, e.g. art_...'),
      }),
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      responses: {
        200: artifactDetailResponseSchema,
        401: errorSchema,
        404: errorSchema,
      },
      summary: 'Get a visible artifact with a presigned download URL',
    },
    presignBackfillArtifacts: {
      method: 'POST',
      path: '/artifacts/backfill/presign',
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      body: presignBackfillArtifactsRequestSchema,
      responses: {
        200: presignBackfillArtifactsResponseSchema,
        401: errorSchema,
        404: errorSchema,
      },
      summary: 'Presign artifact-byte uploads for an existing session thread (backfill)',
    },
    commitBackfillArtifacts: {
      method: 'POST',
      path: '/artifacts/backfill/commit',
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      body: commitBackfillArtifactsRequestSchema,
      responses: {
        200: commitBackfillArtifactsResponseSchema,
        401: errorSchema,
        404: errorSchema,
      },
      summary: 'Promote backfilled artifact bytes into artifact rows',
    },
    getSkillRaw: {
      method: 'GET',
      path: '/skills/:id/raw',
      pathParams: z.object({
        id: z.string().min(1).describe('Stable skill ID, e.g. sk_...'),
      }),
      query: z.object({
        version: z.coerce.number().int().positive().optional().describe('Version timestamp to download. Defaults to latest accepted version.'),
      }),
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      // Plain markdown body. Contract returns string; the server sets
      // text/markdown on the response.
      responses: {
        200: z.string(),
        401: errorSchema,
        403: errorSchema,
        404: errorSchema,
      },
      summary: 'Raw SKILL.md body for a skill (download / install path)',
    },
    installSkill: {
      method: 'POST',
      path: '/skills/:id/installations',
      pathParams: z.object({
        id: z.string().min(1).describe('Workspace skill ID to install'),
      }),
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      body: z.object({}).optional(),
      responses: {
        200: skillInstallationResponseSchema,
        401: errorSchema,
        403: errorSchema,
        404: errorSchema,
      },
      summary: 'Register the current user as an installer of a visible skill',
    },
    uninstallSkill: {
      method: 'DELETE',
      path: '/skills/:id/installation',
      pathParams: z.object({
        id: z.string().min(1).describe('Workspace skill ID to uninstall'),
      }),
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      body: z.object({}).optional(),
      responses: {
        200: skillInstallationSchema,
        401: errorSchema,
      },
      summary: 'Unregister the current user installation for a skill (idempotent)',
    },
    unpublishSkill: {
      method: 'DELETE',
      path: '/skills/:id',
      pathParams: z.object({
        id: z.string().min(1).describe('Workspace skill ID to unpublish'),
      }),
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      body: z.object({}).optional(),
      responses: {
        200: unpublishSkillResponseSchema,
        401: errorSchema,
        403: errorSchema,
        404: errorSchema,
      },
      summary: 'Unpublish an owned workspace skill and remove it from the team catalog',
    },
    updateSkillInstallation: {
      method: 'PATCH',
      path: '/skills/installations/:id',
      pathParams: z.object({
        id: z.string().min(1).describe('Stable skill installation ID, e.g. ski_...'),
      }),
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      body: updateSkillInstallationRequestSchema,
      responses: {
        200: skillInstallationSchema,
        401: errorSchema,
        404: errorSchema,
        422: errorSchema,
      },
      summary: 'Record the accepted skill version installed by the current user',
    },
    listSkillInstallationSync: {
      method: 'GET',
      path: '/skills/installations/sync',
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      responses: {
        200: skillInstallationSyncResponseSchema,
        401: errorSchema,
      },
      summary: 'List installed skills with latest accepted version metadata',
    },
    approveSkillVersion: {
      method: 'POST',
      path: '/skills/:id/versions/:version/approve',
      pathParams: z.object({
        id: z.string().min(1).describe('Workspace skill ID'),
        version: z.coerce.number().int().positive().describe('Proposed version timestamp to approve'),
      }),
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      body: z.object({}).optional(),
      responses: {
        200: skillVersionMutationResponseSchema,
        400: errorSchema,
        401: errorSchema,
        403: errorSchema,
        404: errorSchema,
      },
      summary: 'Approve a proposed skill version and make it latest accepted',
    },
    rejectSkillVersion: {
      method: 'POST',
      path: '/skills/:id/versions/:version/reject',
      pathParams: z.object({
        id: z.string().min(1).describe('Workspace skill ID'),
        version: z.coerce.number().int().positive().describe('Proposed version timestamp to reject'),
      }),
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      body: rejectSkillVersionRequestSchema.optional(),
      responses: {
        200: skillVersionMutationResponseSchema,
        400: errorSchema,
        401: errorSchema,
        403: errorSchema,
        404: errorSchema,
      },
      summary: 'Reject a proposed skill version with an optional owner reason',
    },
    redactThreadBlock: {
      method: 'POST',
      path: '/threads/:threadId/blocks/:blockId/redactions',
      pathParams: z.object({
        threadId: z.string().min(1),
        blockId: z.string().min(1),
      }),
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      body: redactThreadBlockRequestSchema,
      responses: {
        200: threadBlockObjectSchema,
        401: errorSchema,
        403: errorSchema,
        404: errorSchema,
        422: errorSchema,
      },
      summary: 'Permanently redact a character range within a text field of a thread block (author only)',
    },
    updateThreadBlockText: {
      method: 'PATCH',
      path: '/threads/:threadId/blocks/:blockId/text',
      pathParams: z.object({
        threadId: z.string().min(1),
        blockId: z.string().min(1),
      }),
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      body: updateThreadBlockTextRequestSchema,
      responses: {
        200: threadBlockObjectSchema,
        401: errorSchema,
        403: errorSchema,
        404: errorSchema,
        422: errorSchema,
      },
      summary: 'Replace a single text leaf in a thread block with new content (author only)',
    },
    critiqueThreadBlock: {
      method: 'POST',
      path: '/threads/:threadId/blocks/:blockId/critique',
      pathParams: z.object({
        threadId: z.string().min(1),
        blockId: z.string().min(1),
      }),
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      body: z.object({}).optional(),
      responses: {
        200: threadBlockObjectSchema,
        401: errorSchema,
        403: errorSchema,
        404: errorSchema,
        409: errorSchema,
        422: errorSchema,
      },
      summary: 'Critique a plan block once and persist its annotations as nested comment threads (author only)',
    },
    listThreadBlockEdits: {
      method: 'GET',
      path: '/threads/:threadId/blocks/:blockId/edits',
      pathParams: z.object({
        threadId: z.string().min(1),
        blockId: z.string().min(1),
      }),
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      responses: {
        200: threadBlockEditListResponseSchema,
        401: errorSchema,
        404: errorSchema,
      },
      summary: 'List edits made to a thread block (visible to anyone who can see the thread)',
    },
    listThreadDecisions: {
      method: 'GET',
      path: '/threads/:id/decisions',
      pathParams: z.object({
        id: z.string().min(1),
      }),
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      responses: {
        200: threadDecisionListResponseSchema,
        401: errorSchema,
        404: errorSchema,
      },
      summary:
        'List AI-extracted user decisions for a thread, in chronological order',
    },
    updateThread: {
      method: 'PATCH',
      path: '/threads/:id',
      pathParams: z.object({
        id: z.string().min(1),
      }),
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      body: updateThreadRequestSchema,
      responses: {
        200: threadResourceSchema,
        401: errorSchema,
        403: errorSchema,
        404: errorSchema,
      },
      summary: 'Update thread visibility (author only)',
    },
    deleteThread: {
      method: 'DELETE',
      path: '/threads/:id',
      pathParams: z.object({
        id: z.string().min(1),
      }),
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      body: z.object({}).optional(),
      responses: {
        204: z.null(),
        401: errorSchema,
        403: errorSchema,
        404: errorSchema,
      },
      summary: 'Soft-delete a thread (author only)',
    },
    generateThreadCard: {
      method: 'POST',
      path: '/threads/:id/card/generate',
      pathParams: z.object({
        id: z.string().min(1),
      }),
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      body: z.object({}).optional(),
      responses: {
        200: z.object({
          cover_status: threadCoverStatusSchema,
          how_to_status: threadHowToStatusSchema,
        }),
        401: errorSchema,
        403: errorSchema,
        404: errorSchema,
        // Cover re-roll cooldown — caller fired the regenerate
        // endpoint within the cooldown window after the last
        // successful generation. retry_after_seconds is server-
        // computed from cover_generated_at + cooldown - now.
        429: errorSchema.extend({
          retry_after_seconds: z.number().int().nonnegative(),
        }),
      },
      summary:
        "Enqueue cover image + how-to recipe generation for the thread. Author-only. Idempotent — recently-fired statuses are not double-enqueued. Rate-limited: at most one cover generation per minute per thread (cooldown begins when the previous cover finishes). Used to populate cards on legacy threads (created before Lore Cards) and to retry after a 'failed' status or opt back in from 'disabled'.",
    },
    uploadThreadCover: {
      method: 'POST',
      path: '/threads/:id/card/cover/upload',
      pathParams: z.object({
        id: z.string().min(1),
      }),
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      body: threadCoverUploadRequestSchema,
      responses: {
        200: threadCoverUploadResponseSchema,
        401: errorSchema,
        403: errorSchema,
        404: errorSchema,
        413: errorSchema,
        422: errorSchema,
      },
      summary:
        "Upload a custom cover image for a thread. Author or Tanagram admin only. Bytes go to the same storage substrate the AI cover uses (S3 in prod, filesystem in dev), and the threads row is updated atomically with the new cover_storage_url, cover_status='ready', cover_generated_at=now, cover_model='user-uploaded' so subsequent re-rolls / how-to fan-outs treat the upload like any other ready cover.",
    },
    castThreadVote: {
      method: 'PUT',
      path: '/threads/:id/vote',
      pathParams: z.object({
        id: z.string().min(1),
      }),
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      body: castThreadVoteRequestSchema,
      responses: {
        200: threadVoteRollupSchema,
        401: errorSchema,
        403: errorSchema,
        404: errorSchema,
        422: errorSchema,
      },
      summary:
        "Cast or change the caller's up/down vote on a public thread. Idempotent — re-casting the same value is a no-op.",
    },
    clearThreadVote: {
      method: 'DELETE',
      path: '/threads/:id/vote',
      pathParams: z.object({
        id: z.string().min(1),
      }),
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      body: z.object({}).optional(),
      responses: {
        200: threadVoteRollupSchema,
        401: errorSchema,
        404: errorSchema,
      },
      summary:
        "Clear the caller's vote on a thread. No-op when the caller hasn't voted.",
    },
    listThreadComments: {
      method: 'GET',
      path: '/threads/:threadId/comments',
      pathParams: z.object({
        threadId: z.string().min(1),
      }),
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      responses: {
        200: listThreadCommentsResponseSchema,
        403: errorSchema,
        404: errorSchema,
      },
      summary:
        'List all (live + soft-deleted as tombstones) comments on a public thread, flat with parent_comment_id pointers.',
    },
    createThreadComment: {
      method: 'POST',
      path: '/threads/:threadId/comments',
      pathParams: z.object({
        threadId: z.string().min(1),
      }),
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      body: createThreadCommentRequestSchema,
      responses: {
        201: createThreadCommentResponseSchema,
        401: errorSchema,
        403: errorSchema,
        404: errorSchema,
        422: errorSchema,
      },
      summary: 'Post a top-level or reply comment on a public thread.',
    },
    updateThreadComment: {
      method: 'PATCH',
      path: '/threads/:threadId/comments/:commentId',
      pathParams: z.object({
        threadId: z.string().min(1),
        commentId: z.string().min(1),
      }),
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      body: updateThreadCommentRequestSchema,
      responses: {
        200: updateThreadCommentResponseSchema,
        401: errorSchema,
        403: errorSchema,
        404: errorSchema,
        422: errorSchema,
      },
      summary: 'Edit your own thread comment (author only; rejects edits to soft-deleted rows).',
    },
    deleteThreadComment: {
      method: 'DELETE',
      path: '/threads/:threadId/comments/:commentId',
      pathParams: z.object({
        threadId: z.string().min(1),
        commentId: z.string().min(1),
      }),
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      body: z.object({}).optional(),
      responses: {
        200: deleteThreadCommentResponseSchema,
        401: errorSchema,
        403: errorSchema,
        404: errorSchema,
      },
      summary: 'Soft-delete your own thread comment (author only). Idempotent.',
    },
    getThreadPreview: {
      method: 'GET',
      path: '/threads/:id/preview',
      pathParams: z.object({
        id: z.string().min(1),
      }),
      responses: {
        200: threadPreviewResponseSchema,
        404: errorSchema,
      },
      summary: 'Unauthenticated, visibility-redacted metadata for OG / social preview consumers',
    },
    getUser: {
      method: 'GET',
      path: '/users/:id',
      pathParams: z.object({
        id: z.string().min(1),
      }),
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      responses: {
        200: userProfileResourceSchema,
        404: errorSchema,
      },
      summary:
        "Public profile for a user. Optional auth — unauthenticated viewers see only public thread metadata; signed-in viewers see counts scoped to the threads they'd normally be able to access.",
    },
    updateCurrentUser: {
      method: 'PATCH',
      path: '/users/me',
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      body: updateCurrentUserRequestSchema,
      responses: {
        200: userSchema,
        401: errorSchema,
        409: errorSchema,
        422: errorSchema,
      },
      summary: 'Update the authenticated user profile',
    },
    getUserByHandle: {
      method: 'GET',
      path: '/users/by-handle/:handle',
      pathParams: z.object({ handle: handleSchema }),
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      responses: {
        200: profileByHandleResponseSchema,
        404: errorSchema,
      },
      summary:
        "Resolve a public profile by handle — same shape as GET /users/:id but keyed on the user's chosen handle.",
    },
    listMyReferrals: {
      method: 'GET',
      path: '/users/me/referrals',
      headers: z.object({
        authorization: z.string().min(1),
      }),
      responses: {
        200: referralListResponseSchema,
        401: errorSchema,
      },
      summary:
        "Total count plus the most-recent referees the authenticated user has attributed via `/?invited_by=…` invite links. Mirrors the inviter side of users.referred_by_user_id.",
    },
    listUserActivity: {
      method: 'GET',
      path: '/users/:id/activity',
      pathParams: z.object({ id: z.string().min(1) }),
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      query: z.object({
        limit: z.coerce.number().int().positive().max(100).optional(),
      }),
      responses: {
        200: userActivityListResponseSchema,
        404: errorSchema,
      },
      summary:
        "Reverse-chronological activity feed for a user, scoped to what the viewer can see (same visibility rules as listThreads).",
    },
    getUserContributions: {
      method: 'GET',
      path: '/users/:id/contributions',
      pathParams: z.object({ id: z.string().min(1) }),
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      responses: {
        200: userContributionsResponseSchema,
        404: errorSchema,
      },
      summary:
        "Daily contribution counts for the trailing 365 days — counts thread blocks the user authored on threads visible to the viewer.",
    },
    listUserFollowers: {
      method: 'GET',
      path: '/users/:id/followers',
      pathParams: z.object({ id: z.string().min(1) }),
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      responses: {
        200: userFollowListResponseSchema,
        404: errorSchema,
      },
      summary: 'List the users following a given user.',
    },
    listUserFollowing: {
      method: 'GET',
      path: '/users/:id/following',
      pathParams: z.object({ id: z.string().min(1) }),
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      responses: {
        200: userFollowListResponseSchema,
        404: errorSchema,
      },
      summary: 'List the users a given user is following.',
    },
    listFollowSuggestions: {
      method: 'GET',
      path: '/users/:id/follow-suggestions',
      pathParams: z.object({ id: z.string().min(1) }),
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      responses: {
        200: followSuggestionListResponseSchema,
        404: errorSchema,
      },
      summary:
        "Mutual-follow suggestions for a viewer: walks the viewer's followees one hop further and ranks candidates by mutual count. Replaces a 1 → 24 client-side fan-out across `/users/:seed/following`.",
    },
    followUser: {
      method: 'POST',
      path: '/users/:id/follow',
      pathParams: z.object({ id: z.string().min(1) }),
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      body: z.object({}).optional(),
      responses: {
        200: followUserResponseSchema,
        401: errorSchema,
        404: errorSchema,
        409: errorSchema,
        422: errorSchema,
      },
      summary: 'Follow another user. Idempotent — repeating the call is a no-op.',
    },
    unfollowUser: {
      method: 'DELETE',
      path: '/users/:id/follow',
      pathParams: z.object({ id: z.string().min(1) }),
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      body: z.object({}).optional(),
      responses: {
        200: unfollowUserResponseSchema,
        401: errorSchema,
        404: errorSchema,
      },
      summary: 'Unfollow a user. Idempotent — repeating the call is a no-op.',
    },
    listProfilePublishedProjects: {
      method: 'GET',
      path: '/users/:id/projects',
      pathParams: z.object({ id: z.string().min(1) }),
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      responses: {
        200: profilePublishedProjectListResponseSchema,
        404: errorSchema,
      },
      summary: "Projects the user has published to their public profile.",
    },
    publishProject: {
      method: 'POST',
      path: '/projects/:id/publish',
      pathParams: z.object({ id: z.string().min(1) }),
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      body: z.object({}).optional(),
      responses: {
        200: publishProjectResponseSchema,
        401: errorSchema,
        403: errorSchema,
        404: errorSchema,
      },
      summary:
        'Publish a project to the author\'s public profile. Stamps published_at with `now()`. Author-only.',
    },
    unpublishProject: {
      method: 'DELETE',
      path: '/projects/:id/publish',
      pathParams: z.object({ id: z.string().min(1) }),
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      body: z.object({}).optional(),
      responses: {
        200: publishProjectResponseSchema,
        401: errorSchema,
        403: errorSchema,
        404: errorSchema,
      },
      summary: "Remove a project from the author's public profile.",
    },
    uploadProfileImage: {
      method: 'POST',
      path: '/users/me/profile-image',
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      body: profileImageUploadRequestSchema,
      responses: {
        200: profileImageUploadResponseSchema,
        401: errorSchema,
        413: errorSchema,
        422: errorSchema,
      },
      summary:
        "Upload an avatar or banner image (base64-encoded JSON body). The API streams the bytes to its configured storage substrate using its own credentials and returns the canonical storage URL the client passes to PATCH /users/me.",
    },
    listOrganizationMembers: {
      method: 'GET',
      path: '/organization/members',
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      responses: {
        200: organizationMemberListResponseSchema,
        401: errorSchema,
      },
      summary: 'List members of the authenticated user’s organization',
    },
    createOrganizationInvite: {
      method: 'POST',
      path: '/organization/invites',
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      body: createOrganizationInviteRequestSchema,
      responses: {
        201: createOrganizationInviteResponseSchema,
        401: errorSchema,
        403: errorSchema,
        409: errorSchema,
        422: errorSchema,
      },
      summary: 'Invite a teammate to the authenticated user’s workspace via WorkOS AuthKit',
    },
    ensureWorkOSOrganization: {
      method: 'POST',
      path: '/organization/workos/ensure',
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      body: z.object({}).optional(),
      responses: {
        200: ensureWorkOSOrganizationResponseSchema,
        401: errorSchema,
        422: errorSchema,
        503: errorSchema,
      },
      summary:
        'Create or reuse a WorkOS organization for the authenticated user’s non-public email domain and add the user as a member.',
    },
    listRepositories: {
      method: 'GET',
      path: '/repositories',
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      responses: {
        200: repositoryListResponseSchema,
        401: errorSchema,
        403: errorSchema,
      },
      summary:
        'List repositories known to the authenticated user’s organization across threads and building blocks',
    },
    createBuildingBlockSnapshots: {
      method: 'POST',
      path: '/building_blocks',
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      body: createBuildingBlockSnapshotsRequestSchema,
      responses: {
        201: createBuildingBlockSnapshotsResponseSchema,
        401: errorSchema,
        403: errorSchema,
      },
      summary: 'Create building block snapshots for the authenticated user’s organization',
    },
    getPlan: {
      method: 'GET',
      path: '/plans/:id',
      pathParams: z.object({
        id: z.string().min(1),
      }),
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      responses: {
        200: getPlanResponseSchema,
        401: errorSchema,
        403: errorSchema,
        404: errorSchema,
      },
      summary:
        'Load a thread-backed plan container visible to the authenticated user, including all visible plan revisions in that thread (only the latest revision carries body + prosemirror_json; older revisions are metadata-only and load on demand via getPlanRevision)',
    },
    getPlanRevision: {
      method: 'GET',
      path: '/plans/:id/revisions/:revisionId',
      pathParams: z.object({
        id: z.string().min(1),
        revisionId: z.string().min(1),
      }),
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      responses: {
        200: getPlanRevisionResponseSchema,
        401: errorSchema,
        403: errorSchema,
        404: errorSchema,
      },
      summary:
        'Load a single revision body + prosemirror_json + comment threads for a thread-backed plan container. Used to fetch non-latest revisions on demand without paying for every revision when the plan is first opened',
    },
    createPlanCommentThread: {
      method: 'POST',
      path: '/plans/:id/comment-threads',
      pathParams: z.object({
        id: z.string().min(1),
      }),
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      body: createPlanCommentThreadRequestSchema,
      responses: {
        201: createPlanCommentThreadResponseSchema,
        401: errorSchema,
        403: errorSchema,
        404: errorSchema,
        422: errorSchema,
      },
      summary:
        'Create a new inline comment thread on a specific revision inside a thread-backed plan container',
    },
    createPlanComment: {
      method: 'POST',
      path: '/plans/:id/comment-threads/:threadId/comments',
      pathParams: z.object({
        id: z.string().min(1),
        threadId: z.string().min(1),
      }),
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      body: createPlanCommentRequestSchema,
      responses: {
        201: createPlanCommentResponseSchema,
        401: errorSchema,
        403: errorSchema,
        404: errorSchema,
        409: errorSchema,
      },
      summary: 'Reply to an inline comment thread on a thread-backed plan container',
    },
    updatePlanCommentThread: {
      method: 'PATCH',
      path: '/plans/:id/comment-threads/:threadId',
      pathParams: z.object({
        id: z.string().min(1),
        threadId: z.string().min(1),
      }),
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      body: updatePlanCommentThreadRequestSchema,
      responses: {
        200: updatePlanCommentThreadResponseSchema,
        401: errorSchema,
        403: errorSchema,
        404: errorSchema,
      },
      summary: 'Resolve or reopen an inline comment thread on a thread-backed plan container',
    },
    listPlans: {
      method: 'GET',
      path: '/plans',
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      query: listPlansQuerySchema,
      responses: {
        200: planListResponseSchema,
        401: errorSchema,
        403: errorSchema,
      },
      summary:
        'List thread-backed plan containers visible to the authenticated user in their organization, ordered by latest visible revision and optionally filtered by author or repository',
    },
    listBuildingBlockSnapshots: {
      method: 'GET',
      path: '/building_blocks',
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      query: listBuildingBlockSnapshotsQuerySchema,
      responses: {
        200: buildingBlockSnapshotListResponseSchema,
        401: errorSchema,
        403: errorSchema,
      },
      summary:
        'List building block snapshots for the authenticated user’s organization and repo origin path',
    },
    listUploadSessions: {
      method: 'GET',
      path: '/upload_sessions',
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      responses: {
        200: uploadSessionListResponseSchema,
        401: errorSchema,
      },
      summary: 'List recent upload sessions for the authenticated user',
    },
    listUploadApiKeys: {
      method: 'GET',
      path: '/upload_api_keys',
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      query: listUploadApiKeysQuerySchema,
      responses: {
        200: uploadApiKeyListResponseSchema,
        401: errorSchema,
        403: errorSchema,
      },
      summary: 'List Upload API keys owned by the authenticated user in a workspace',
    },
    createUploadApiKey: {
      method: 'POST',
      path: '/upload_api_keys',
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      body: createUploadApiKeyRequestSchema,
      responses: {
        201: createUploadApiKeyResponseSchema,
        400: errorSchema,
        401: errorSchema,
        403: errorSchema,
      },
      summary: 'Create an Upload API key and return the raw key exactly once',
    },
    revokeUploadApiKey: {
      method: 'POST',
      path: '/upload_api_keys/:id/revoke',
      pathParams: z.object({
        id: z.string().min(1).describe('Upload API key id'),
      }),
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      body: z.object({}).optional(),
      responses: {
        200: uploadApiKeyResourceSchema,
        401: errorSchema,
        403: errorSchema,
        404: errorSchema,
      },
      summary: 'Revoke an Upload API key owned by the authenticated user',
    },
    createUploadSession: {
      method: 'POST',
      path: '/upload_sessions',
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      body: z.object({
        thread_files: z.array(threadFileRequestSchema).optional(),
        skill_files: z.array(skillFileRequestSchema).optional(),
        upload_metadata: uploadMetadataSchema.optional(),
      }),
      responses: {
        201: uploadSessionResponseSchema,
        400: errorSchema,
        401: errorSchema,
      },
      summary: 'Create an upload session with presigned URLs for file uploads',
    },
    completeUploadSession: {
      method: 'POST',
      path: '/upload_sessions/:id/complete',
      pathParams: z.object({
        id: z.string().min(1),
      }),
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      body: z.object({
        thread_files: z.array(completeThreadFileRequestSchema).optional(),
        skill_files: z.array(completeSkillFileRequestSchema).optional(),
      }),
      responses: {
        200: completeUploadSessionResponseSchema,
        400: errorSchema,
        401: errorSchema,
        403: errorSchema,
        404: errorSchema,
        409: errorSchema,
        422: errorSchema,
      },
      summary: 'Complete an upload session after files have been uploaded to storage',
    },
    adminStats: {
      method: 'GET',
      path: '/admin/stats',
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      responses: {
        200: adminStatsSchema,
        401: errorSchema,
        403: errorSchema,
      },
      summary: 'Cross-org operational counts. Tanagram admins only.',
    },
    adminListOrganizations: {
      method: 'GET',
      path: '/admin/organizations',
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      responses: {
        200: adminOrganizationListResponseSchema,
        401: errorSchema,
        403: errorSchema,
      },
      summary: 'List every organization with member and thread counts. Tanagram admins only.',
    },
    adminListUsers: {
      method: 'GET',
      path: '/admin/users',
      query: adminListUsersQuerySchema,
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      responses: {
        200: adminUserListResponseSchema,
        401: errorSchema,
        403: errorSchema,
      },
      summary: 'List every user with their org memberships and thread count. Tanagram admins only.',
    },
    adminListThreads: {
      method: 'GET',
      path: '/admin/threads',
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      query: adminListThreadsQuerySchema,
      responses: {
        200: adminThreadListResponseSchema,
        401: errorSchema,
        403: errorSchema,
      },
      summary: 'List live threads across every organization. Tanagram admins only.',
    },
    adminListTweetLeads: {
      method: 'GET',
      path: '/admin/tweet-leads',
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      responses: {
        200: adminTweetLeadListResponseSchema,
        401: errorSchema,
        403: errorSchema,
      },
      summary:
        'List unreplied tweet leads from the last 24h, ranked by relevance. Tanagram admins only.',
    },
    adminUpdateTweetLead: {
      method: 'PATCH',
      path: '/admin/tweet-leads/:id',
      pathParams: z.object({ id: z.string().min(1) }),
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      body: adminUpdateTweetLeadRequestSchema,
      responses: {
        200: tweetLeadSchema,
        401: errorSchema,
        403: errorSchema,
        404: errorSchema,
      },
      summary: 'Update a tweet lead status (replied/dismissed/new). Tanagram admins only.',
    },
    adminLookupThread: {
      method: 'GET',
      path: '/admin/threads/lookup',
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      query: adminLookupThreadQuerySchema,
      responses: {
        200: adminThreadLookupResponseSchema,
        400: errorSchema,
        401: errorSchema,
        403: errorSchema,
        404: errorSchema,
      },
      summary: 'Look up a thread by thread id, thread file id, or harness session id. Tanagram admins only.',
    },
    adminForceFollow: {
      method: 'POST',
      path: '/admin/connections/follow',
      headers: z.object({ authorization: z.string().min(1).optional() }),
      body: adminForceFollowRequestSchema,
      responses: {
        200: adminForceFollowResponseSchema,
        401: errorSchema,
        403: errorSchema,
        404: errorSchema,
        422: errorSchema,
      },
      summary: 'Force follower->followee edge. Admin-only and non-destructive.',
    },
    adminHardDeleteThread: {
      method: 'DELETE',
      path: '/admin/threads/:id',
      pathParams: z.object({
        id: z.string().min(1),
      }),
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      body: z.object({}).optional(),
      responses: {
        204: z.null(),
        401: errorSchema,
        403: errorSchema,
        404: errorSchema,
      },
      summary: 'Permanently remove a thread. Tanagram admins only.',
    },
    adminDeleteSkill: {
      method: 'DELETE',
      path: '/admin/skills/:id',
      pathParams: z.object({
        id: z.string().min(1).describe('Workspace skill ID, e.g. sk_...'),
      }),
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      body: z.object({}).optional(),
      responses: {
        200: adminDeleteSkillResponseSchema,
        401: errorSchema,
        403: errorSchema,
        404: errorSchema,
      },
      summary: 'Permanently remove a skill and its sync records. Tanagram admins only.',
    },
    adminReparseThreadFile: {
      method: 'POST',
      path: '/admin/thread-files/:id/reparse',
      pathParams: z.object({
        id: z.string().min(1).describe('Thread file id, e.g. tf_xxx.'),
      }),
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      body: z.object({}).optional(),
      responses: {
        200: adminReparseThreadFileResponseSchema,
        401: errorSchema,
        403: errorSchema,
        404: errorSchema,
      },
      summary: 'Re-enqueue parsing for a thread file. Tanagram admins only.',
    },
    listCardFeed: {
      method: 'GET',
      path: '/cards/feed',
      // Public endpoint — landing-page card scroll is the use case,
      // so anonymous callers get the same response as signed-in
      // ones. Threads are filtered to public + cover_status='ready'
      // server-side.
      query: z.object({
        limit: z.coerce.number().int().min(1).max(48).optional(),
      }),
      responses: {
        200: cardFeedResponseSchema,
      },
      summary:
        "Slim feed of public threads with a ready cover image, ordered by cover_generated_at descending. Powers the landing-page UGC card scroll. Returns presigned cover URLs inline so the grid renders without a follow-up round trip per card.",
    },
    listDiscoverThreads: {
      method: 'GET',
      path: '/discover/threads',
      // Auth is optional: signed-out callers see the feed with
      // viewer_vote=0; signed-in callers get viewer_vote populated so
      // the UI can render their existing vote state.
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      query: listDiscoverThreadsQuerySchema,
      responses: {
        200: discoverThreadListResponseSchema,
      },
      summary:
        'Feed of public threads with sort, harness filter, search, and cursor pagination. Auth is optional and populates viewer_vote.',
    },
    discoverStats: {
      method: 'GET',
      path: '/discover/stats',
      responses: {
        200: discoverStatsSchema,
      },
      summary:
        'Unauthenticated rollup of public-thread counts for the Discover header.',
    },
    createWaitlistEntry: {
      method: 'POST',
      path: '/waitlist_entries',
      body: createWaitlistEntryRequestSchema,
      responses: {
        201: waitlistEntrySchema,
        409: errorSchema,
      },
      summary:
        'Create an unauthenticated waitlist entry keyed by (location, contact).',
    },
    submitContactMessage: {
      method: 'POST',
      path: '/contact',
      body: submitContactMessageRequestSchema,
      responses: {
        200: submitContactMessageResponseSchema,
        429: errorSchema,
        503: errorSchema,
      },
      summary:
        'Submit a contact-form message from the marketing site; emails the Lore team.',
    },
    createFeedback: {
      method: 'POST',
      path: '/feedback',
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      body: createFeedbackRequestSchema,
      responses: {
        201: feedbackEntrySchema,
        401: errorSchema,
        422: errorSchema,
        429: errorSchema,
      },
      summary:
        'Submit in-app feedback. Persists to lore.feedback_entries and best-effort posts to the #lore-feedback Slack channel. Rate-limited to 30 submissions/hour per user.',
    },
    createShareToken: {
      method: 'POST',
      path: '/threads/:id/share-token',
      pathParams: z.object({
        id: z.string().min(1),
      }),
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      body: z.object({}).optional(),
      responses: {
        201: shareTokenResponseSchema,
        401: errorSchema,
        403: errorSchema,
        404: errorSchema,
        409: errorSchema,
      },
      summary:
        'Mint a short share-link token for a public thread. Author only. The token powers `/s/:token` short URLs and enables k-factor attribution.',
    },
    resolveShareToken: {
      method: 'GET',
      path: '/referrals/resolve/:token',
      pathParams: z.object({
        token: z.string().min(1).max(16),
      }),
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      query: resolveShareTokenQuerySchema,
      responses: {
        200: shareTokenResponseSchema,
        404: errorSchema,
      },
      summary:
        'Resolve a share-link token to its thread and record the view. Works for signed-out viewers; if a bearer token is present we attribute the view to that Lore user for analytics.',
    },
    adminGrowth: {
      method: 'GET',
      path: '/admin/growth',
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      responses: {
        200: adminGrowthResponseSchema,
        401: errorSchema,
        403: errorSchema,
      },
      summary:
        'Share-link k-factor, funnel, sparkline, and top sharers by window. Tanagram admins only.',
    },
    adminActiveUsers: {
      method: 'GET',
      path: '/admin/active-users',
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      responses: {
        200: adminActiveUsersResponseSchema,
        401: errorSchema,
        403: errorSchema,
      },
      summary:
        'DAU/WAU/MAU/all-time active-user buckets — union of oauth token issuance and thread-event spine. Tanagram admins only.',
    },
    adminReferrals: {
      method: 'GET',
      path: '/admin/referrals',
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      responses: {
        200: adminReferralsAnalyticsResponseSchema,
        401: errorSchema,
        403: errorSchema,
      },
      summary:
        'Cross-organization invite-link funnel: lifetime + 30-day counts, 60-day daily timeseries, top inviters, recent attributions. Tanagram admins only.',
    },
    adminOnboardingSources: {
      method: 'GET',
      path: '/admin/onboarding/sources',
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      responses: {
        200: adminOnboardingSourcesResponseSchema,
        401: errorSchema,
        403: errorSchema,
      },
      summary:
        "Signed-up persons grouped by PostHog `initial_source` first-touch attribution. Powers bucket 1 of /admin's Onboarding flow tab; sourced via HogQL.",
    },
    adminRunCritiquePlayground: {
      method: 'POST',
      path: '/admin/critique-playground/run',
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      body: adminCritiquePlaygroundRunRequestSchema,
      responses: {
        200: adminCritiquePlaygroundRunResponseSchema,
        401: errorSchema,
        403: errorSchema,
        409: errorSchema,
      },
      summary:
        'Admin-only critique playground that saves markdown to a file:// plan artifact and returns parsed + critiqued plan blocks.',
    },
    adminListEmailTemplates: {
      method: 'GET',
      path: '/admin/email-templates',
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      responses: {
        200: adminEmailTemplateListResponseSchema,
        401: errorSchema,
        403: errorSchema,
      },
      summary:
        'List transactional email templates and their editable preview fields. Tanagram admins only.',
    },
    adminPreviewEmailTemplate: {
      method: 'POST',
      path: '/admin/email-templates/:id/preview',
      pathParams: z.object({
        id: z.string().min(1).describe('Email template id.'),
      }),
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      body: adminEmailTemplatePreviewRequestSchema,
      responses: {
        200: renderedEmailSchema,
        400: errorSchema,
        401: errorSchema,
        403: errorSchema,
        404: errorSchema,
      },
      summary:
        'Render a transactional email template with supplied values. Tanagram admins only.',
    },
    adminSendEmailTemplate: {
      method: 'POST',
      path: '/admin/email-templates/:id/send',
      pathParams: z.object({
        id: z.string().min(1).describe('Email template id.'),
      }),
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      body: adminEmailTemplateSendRequestSchema,
      responses: {
        200: adminEmailTemplateSendResponseSchema,
        400: errorSchema,
        401: errorSchema,
        403: errorSchema,
        404: errorSchema,
        503: errorSchema,
      },
      summary:
        'Send a transactional email template through Resend. Tanagram admins only.',
    },
    getBillingState: {
      method: 'GET',
      path: '/billing/state',
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      responses: {
        200: billingStateResponseSchema,
        401: errorSchema,
      },
      summary: 'Resolve the caller\'s plan, features, seat count, and any admin override.',
    },
    createBillingCheckout: {
      method: 'POST',
      path: '/billing/checkout',
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      body: createCheckoutSessionRequestSchema,
      responses: {
        200: createCheckoutSessionResponseSchema,
        400: errorSchema,
        401: errorSchema,
        403: errorSchema,
        503: errorSchema,
      },
      summary: 'Start a Stripe Checkout session for Creator ($29/mo) or Team ($19/seat/mo, min 2).',
    },
    createBillingPortal: {
      method: 'POST',
      path: '/billing/portal',
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      body: z.object({
        subject_type: z.enum(['user', 'organization']).default('user'),
      }).optional(),
      responses: {
        200: createBillingPortalResponseSchema,
        401: errorSchema,
        404: errorSchema,
        503: errorSchema,
      },
      summary: 'Open the Stripe customer portal so the subject can manage their subscription.',
    },
    updateTeamSeats: {
      method: 'PATCH',
      path: '/billing/team-seats',
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      body: updateTeamSeatsRequestSchema,
      responses: {
        200: billingSubjectSummarySchema,
        400: errorSchema,
        401: errorSchema,
        403: errorSchema,
        404: errorSchema,
        503: errorSchema,
      },
      summary: 'Adjust the caller-organization\'s Team seat quantity (admin of that org only).',
    },
    getCreditSettings: {
      method: 'GET',
      path: '/billing/credits/settings',
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      responses: {
        200: creditSettingsResponseSchema,
        400: errorSchema,
        401: errorSchema,
        403: errorSchema,
        503: errorSchema,
      },
      summary: 'Get the caller-organization\'s credit pool balance + auto-recharge settings.',
    },
    updateCreditSettings: {
      method: 'PUT',
      path: '/billing/credits/settings',
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      body: updateCreditSettingsRequestSchema,
      responses: {
        200: creditSettingsResponseSchema,
        400: errorSchema,
        401: errorSchema,
        403: errorSchema,
        503: errorSchema,
      },
      summary: 'Update the caller-organization\'s auto-recharge settings (billing admin only).',
    },
    createCreditTopUp: {
      method: 'POST',
      path: '/billing/credits/topup',
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      body: createCreditTopUpRequestSchema,
      responses: {
        200: createCreditTopUpResponseSchema,
        400: errorSchema,
        401: errorSchema,
        403: errorSchema,
        503: errorSchema,
      },
      summary: 'Manually top up the caller-organization\'s credit pool by charging the saved card.',
    },
    adminListBilling: {
      method: 'GET',
      path: '/admin/billing',
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      responses: {
        200: adminBillingOverviewResponseSchema,
        401: errorSchema,
        403: errorSchema,
      },
      summary: 'List every user and org with current plan + override for the admin panel.',
    },
    adminListCreditPools: {
      method: 'GET',
      path: '/admin/billing/credits',
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      responses: {
        200: adminCreditPoolsResponseSchema,
        401: errorSchema,
        403: errorSchema,
      },
      summary: 'Per-team credit-pool utilization, blended margin, breakage, and overage.',
    },
    adminSetUserPlanOverride: {
      method: 'PUT',
      path: '/admin/billing/users/:id/override',
      pathParams: z.object({
        id: z.string().min(1),
      }),
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      body: adminPlanOverrideRequestSchema,
      responses: {
        200: adminBillingSubjectSchema,
        401: errorSchema,
        403: errorSchema,
        404: errorSchema,
      },
      summary: 'Admin override for a user\'s plan tier. Null clears the override.',
    },
    adminSetOrganizationPlanOverride: {
      method: 'PUT',
      path: '/admin/billing/organizations/:id/override',
      pathParams: z.object({
        id: z.string().min(1),
      }),
      headers: z.object({
        authorization: z.string().min(1).optional(),
      }),
      body: adminPlanOverrideRequestSchema,
      responses: {
        200: adminBillingSubjectSchema,
        401: errorSchema,
        403: errorSchema,
        404: errorSchema,
      },
      summary: 'Admin override for an organization\'s plan tier. Null clears the override.',
    },
    projects: projectsContract,
    regions: regionsContract,
    entities: entitiesContract,
    docs: docsContract,
    mentions: mentionsContract,
    favorites: favoritesContract,
    search: searchContract,
  },
  {
    pathPrefix: '/api',
  }
);

export * from './projects.ts';
export * from './regions.ts';
export * from './entities.ts';
export * from './docs.ts';
export * from './mentions.ts';
export * from './favorites.ts';
export * from './search.ts';

export type LiveThreadAuthor = z.infer<typeof liveThreadAuthorSchema>;
export type LiveThreadObject = z.infer<typeof liveThreadObjectSchema>;
export type LiveThreadListResponse = z.infer<typeof liveThreadListResponseSchema>;
export type CliAuthConfigResponse = z.infer<typeof cliAuthConfigResponseSchema>;
export type CliStatusResponse = z.infer<typeof cliStatusResponseSchema>;
export type RecordHeartbeatRequest = z.infer<typeof recordHeartbeatRequestSchema>;
export type RecordHeartbeatResponse = z.infer<typeof recordHeartbeatResponseSchema>;
export type RecordProductPresenceResponse = z.infer<typeof recordProductPresenceResponseSchema>;
export type RecordCliHeartbeatRequest = z.infer<typeof recordCliHeartbeatRequestSchema>;
export type RecordCliHeartbeatResponse = z.infer<typeof recordCliHeartbeatResponseSchema>;

export type Message = z.infer<typeof messageSchema>;
export type ThreadSummary = z.infer<typeof threadSummarySchema>;
export type ThreadDetails = z.infer<typeof threadDetailsSchema>;
export type WhoAmIResponse = z.infer<typeof whoAmIResponseSchema>;
export type PosthogUserProperties = z.infer<typeof posthogUserPropertiesSchema>;
export type PosthogOrganizationContext = z.infer<
  typeof posthogOrganizationContextSchema
>;
export type User = z.infer<typeof userSchema>;
export type Harness = z.infer<typeof harnessSchema>;
export type TranscriptUploadHarness = z.infer<typeof transcriptUploadHarnessSchema>;
export type ThreadFileRequest = z.infer<typeof threadFileRequestSchema>;
export type ThreadFileResponse = z.infer<typeof threadFileResponseSchema>;
export type UploadSessionResponse = z.infer<typeof uploadSessionResponseSchema>;
export type UploadApiKeyResource = z.infer<typeof uploadApiKeyResourceSchema>;
export type UploadApiKeyListResponse = z.infer<typeof uploadApiKeyListResponseSchema>;
export type CreateUploadApiKeyRequest = z.infer<typeof createUploadApiKeyRequestSchema>;
export type CreateUploadApiKeyResponse = z.infer<typeof createUploadApiKeyResponseSchema>;
export type ListUploadApiKeysQuery = z.infer<typeof listUploadApiKeysQuerySchema>;
export type CreateThreadRequest = z.infer<typeof createThreadRequestSchema>;
export type CreateThreadResponse = z.infer<typeof createThreadResponseSchema>;
export type ThreadResource = z.infer<typeof threadResourceSchema>;
export type ThreadDecisionResource = z.infer<typeof threadDecisionResourceSchema>;
export type ThreadDecisionBlockStatus = z.infer<typeof threadDecisionBlockStatusSchema>;
export type ThreadDecisionListResponse = z.infer<typeof threadDecisionListResponseSchema>;
export type ThreadDecisionKind = z.infer<typeof threadDecisionKindSchema>;
export type DecisionAnalysisStatus = z.infer<typeof decisionAnalysisStatusSchema>;
export type ForkThreadRequest = z.infer<typeof forkThreadRequestSchema>;
export type ForkSummary = z.infer<typeof forkSummarySchema>;
export type AskThreadsRequest = z.infer<typeof askThreadsRequestSchema>;
export type AskThreadsResponse = z.infer<typeof askThreadsResponseSchema>;
export type AskThreadsSource = z.infer<typeof askThreadsSourceSchema>;
export type AskThreadsSourceDecision = z.infer<typeof askThreadsSourceDecisionSchema>;
export type AskThreadsTrace = z.infer<typeof askThreadsTraceSchema>;
export type AskThreadsTraceStep = z.infer<typeof askThreadsTraceStepSchema>;
export type ThreadCoverStatus = z.infer<typeof threadCoverStatusSchema>;
export type ThreadHowToStatus = z.infer<typeof threadHowToStatusSchema>;
export type ThreadHowToStep = z.infer<typeof threadHowToStepSchema>;
export type ThreadCardCover = z.infer<typeof threadCardCoverSchema>;
export type ThreadCardHowTo = z.infer<typeof threadCardHowToSchema>;
export type ThreadCard = z.infer<typeof threadCardSchema>;
export type CardFeedItem = z.infer<typeof cardFeedItemSchema>;
export type CardFeedResponse = z.infer<typeof cardFeedResponseSchema>;
export type CompleteThreadFileRequest = z.infer<typeof completeThreadFileRequestSchema>;
export type CompleteThreadFileResponse = z.infer<typeof completeThreadFileResponseSchema>;
export type CompleteUploadSessionResponse = z.infer<typeof completeUploadSessionResponseSchema>;
export type ResolveThreadShareHighlightResponse = z.infer<typeof resolveThreadShareHighlightResponseSchema>;
export type ThreadListAuthor = z.infer<typeof threadListAuthorSchema>;
export type ThreadBlockCommentAuthor = z.infer<typeof threadBlockCommentAuthorSchema>;
export type ThreadBlockComment = z.infer<typeof threadBlockCommentSchema>;
export type ThreadBlockCommentThread = z.infer<typeof threadBlockCommentThreadSchema>;
export type ThreadBlockObject = z.infer<typeof threadBlockObjectSchema>;
export type ThreadBlockListResponse = z.infer<typeof threadBlockListResponseSchema>;
export type ProsemirrorJson = z.infer<typeof prosemirrorJsonSchema>;
export type PlanBlock = z.infer<typeof planBlockSchema>;
export type PlanRevision = z.infer<typeof planRevisionSchema>;
export type ThreadListObject = z.infer<typeof threadListObjectSchema>;
export type ThreadListResponse = z.infer<typeof threadListResponseSchema>;
export type PlanListObject = z.infer<typeof planListObjectSchema>;
export type PlanCommentAuthor = z.infer<typeof planCommentAuthorSchema>;
export type PlanComment = z.infer<typeof planCommentSchema>;
export type PlanCommentThread = z.infer<typeof planCommentThreadSchema>;
export type CreateThreadBlockCommentThreadRequest = z.infer<typeof createThreadBlockCommentThreadRequestSchema>;
export type CreateThreadBlockCommentThreadResponse = z.infer<typeof createThreadBlockCommentThreadResponseSchema>;
export type CreatePlanCommentThreadRequest = z.infer<typeof createPlanCommentThreadRequestSchema>;
export type CreatePlanCommentThreadResponse = z.infer<typeof createPlanCommentThreadResponseSchema>;
export type CreateThreadBlockCommentRequest = z.infer<typeof createThreadBlockCommentRequestSchema>;
export type CreateThreadBlockCommentResponse = z.infer<typeof createThreadBlockCommentResponseSchema>;
export type CreatePlanCommentRequest = z.infer<typeof createPlanCommentRequestSchema>;
export type CreatePlanCommentResponse = z.infer<typeof createPlanCommentResponseSchema>;
export type UpdateThreadBlockCommentThreadRequest = z.infer<typeof updateThreadBlockCommentThreadRequestSchema>;
export type UpdateThreadBlockCommentThreadResponse = z.infer<typeof updateThreadBlockCommentThreadResponseSchema>;
export type UpdatePlanCommentThreadRequest = z.infer<typeof updatePlanCommentThreadRequestSchema>;
export type UpdatePlanCommentThreadResponse = z.infer<typeof updatePlanCommentThreadResponseSchema>;
export type GetPlanResponse = z.infer<typeof getPlanResponseSchema>;
export type GetPlanRevisionResponse = z.infer<typeof getPlanRevisionResponseSchema>;
export type PlanListResponse = z.infer<typeof planListResponseSchema>;
export type ListPlansQuery = z.infer<typeof listPlansQuerySchema>;
export type CreateBuildingBlockSnapshot = z.infer<typeof createBuildingBlockSnapshotSchema>;
export type CreateBuildingBlockSnapshotsRequest = z.infer<typeof createBuildingBlockSnapshotsRequestSchema>;
export type BuildingBlockSnapshot = z.infer<typeof buildingBlockSnapshotSchema>;
export type CreateBuildingBlockSnapshotsResponse = z.infer<typeof createBuildingBlockSnapshotsResponseSchema>;
export type BuildingBlockSnapshotListResponse = z.infer<typeof buildingBlockSnapshotListResponseSchema>;
export type ListBuildingBlockSnapshotsQuery = z.infer<typeof listBuildingBlockSnapshotsQuerySchema>;
export type RepositorySource = z.infer<typeof repositorySourceSchema>;
export type RepositoryResource = z.infer<typeof repositoryResourceSchema>;
export type RepositoryListResponse = z.infer<typeof repositoryListResponseSchema>;
export type UpdateCurrentUserRequest = z.infer<typeof updateCurrentUserRequestSchema>;
export type OrganizationMember = z.infer<typeof organizationMemberSchema>;
export type OrganizationMemberListResponse = z.infer<typeof organizationMemberListResponseSchema>;
export type UserProfileResource = z.infer<typeof userProfileResourceSchema>;
export type CreateOrganizationInviteRequest = z.infer<typeof createOrganizationInviteRequestSchema>;
export type CreateOrganizationInviteResponse = z.infer<typeof createOrganizationInviteResponseSchema>;
export type EnsureWorkOSOrganizationResponse = z.infer<typeof ensureWorkOSOrganizationResponseSchema>;
export type CreateWaitlistEntryRequest = z.infer<typeof createWaitlistEntryRequestSchema>;
export type WaitlistEntry = z.infer<typeof waitlistEntrySchema>;
export type SubmitContactMessageRequest = z.infer<typeof submitContactMessageRequestSchema>;
export type FeedbackType = z.infer<typeof feedbackTypeSchema>;
export type CreateFeedbackRequest = z.infer<typeof createFeedbackRequestSchema>;
export type FeedbackEntry = z.infer<typeof feedbackEntrySchema>;
export type UpdateThreadRequest = z.infer<typeof updateThreadRequestSchema>;
export type ThreadPreviewResponse = z.infer<typeof threadPreviewResponseSchema>;
export type UploadSessionSummary = z.infer<typeof uploadSessionSummarySchema>;
export type UploadSessionListResponse = z.infer<typeof uploadSessionListResponseSchema>;
export type ListThreadsQuery = z.infer<typeof listThreadsQuerySchema>;
export type AdminStats = z.infer<typeof adminStatsSchema>;
export type AdminOrganization = z.infer<typeof adminOrganizationSchema>;
export type AdminOrganizationListResponse = z.infer<typeof adminOrganizationListResponseSchema>;
export type AdminUser = z.infer<typeof adminUserSchema>;
export type AdminUserOrganization = z.infer<typeof adminUserOrganizationSchema>;
export type AdminUserListResponse = z.infer<typeof adminUserListResponseSchema>;
export type AdminListUsersQuery = z.infer<typeof adminListUsersQuerySchema>;
export type AdminForceFollowRequest = z.infer<typeof adminForceFollowRequestSchema>;
export type AdminForceFollowResponse = z.infer<typeof adminForceFollowResponseSchema>;
export type AdminDeleteSkillResponse = z.infer<typeof adminDeleteSkillResponseSchema>;
export type AdminThread = z.infer<typeof adminThreadSchema>;
export type AdminThreadFile = z.infer<typeof adminThreadFileSchema>;
export type AdminThreadLookupResponse = z.infer<typeof adminThreadLookupResponseSchema>;
export type AdminThreadListResponse = z.infer<typeof adminThreadListResponseSchema>;
export type AdminListThreadsQuery = z.infer<typeof adminListThreadsQuerySchema>;
export type AdminLookupThreadQuery = z.infer<typeof adminLookupThreadQuerySchema>;
export type AdminReparseThreadFileResponse = z.infer<typeof adminReparseThreadFileResponseSchema>;
export type RedactThreadBlockRequest = z.infer<typeof redactThreadBlockRequestSchema>;
export type ArtifactSource = z.infer<typeof artifactSourceSchema>;
export type ArtifactKind = z.infer<typeof artifactKindSchema>;
export type ArtifactAuthor = z.infer<typeof artifactAuthorSchema>;
export type ArtifactSummary = z.infer<typeof artifactSummarySchema>;
export type ArtifactIndexResponse = z.infer<typeof artifactIndexResponseSchema>;
export type ArtifactDetailResponse = z.infer<typeof artifactDetailResponseSchema>;
export type PresignBackfillArtifactsRequest = z.infer<typeof presignBackfillArtifactsRequestSchema>;
export type PresignBackfillArtifactsResponse = z.infer<typeof presignBackfillArtifactsResponseSchema>;
export type CommitBackfillArtifactsRequest = z.infer<typeof commitBackfillArtifactsRequestSchema>;
export type CommitBackfillArtifactsResponse = z.infer<typeof commitBackfillArtifactsResponseSchema>;
export type ListArtifactsQuery = z.infer<typeof listArtifactsQuerySchema>;
export type SkillSummary = z.infer<typeof skillSummarySchema>;
export type SkillIndexResponse = z.infer<typeof skillIndexResponseSchema>;
export type ReconcileLocalSkillsQuery = z.infer<typeof reconcileLocalSkillsQuerySchema>;
export type LocalSkillReconciliationSkill = z.infer<typeof localSkillReconciliationSkillSchema>;
export type LocalSkillReconciliationResponse = z.infer<typeof localSkillReconciliationResponseSchema>;
export type SkillDetailThread = z.infer<typeof skillDetailThreadSchema>;
export type SkillDetailVersion = z.infer<typeof skillDetailVersionSchema>;
export type SkillDetailResponse = z.infer<typeof skillDetailResponseSchema>;
export type SharedSkillPreview = z.infer<typeof sharedSkillPreviewSchema>;
export type SetSkillVisibilityRequest = z.infer<typeof setSkillVisibilityRequestSchema>;
export type SkillInstallation = z.infer<typeof skillInstallationSchema>;
export type SkillInstallationResponse = z.infer<typeof skillInstallationResponseSchema>;
export type SkillVersionMutationResponse = z.infer<typeof skillVersionMutationResponseSchema>;
export type SkillInstallationSyncResponse = z.infer<typeof skillInstallationSyncResponseSchema>;
export type SkillVisibility = z.infer<typeof skillVisibilitySchema>;
export type SkillRootKind = z.infer<typeof skillRootKindSchema>;
export type LocalSkillScannerSkill = z.infer<typeof localSkillScannerSkillSchema>;
export type LocalSkillScannerOutput = z.infer<typeof localSkillScannerOutputSchema>;
export type SkillVersionStatus = z.infer<typeof skillVersionStatusSchema>;
export type SkillSyncAction = z.infer<typeof skillSyncActionSchema>;
export type SkillAuthor = z.infer<typeof skillAuthorSchema>;
export type UnpublishSkillResponse = z.infer<typeof unpublishSkillResponseSchema>;
export type UpdateThreadBlockTextRequest = z.infer<typeof updateThreadBlockTextRequestSchema>;
export type ThreadBlockEdit = z.infer<typeof threadBlockEditSchema>;
export type ThreadBlockEditKind = z.infer<typeof threadBlockEditKindSchema>;
export type ThreadBlockEditListResponse = z.infer<typeof threadBlockEditListResponseSchema>;
export type DiscoverSort = z.infer<typeof discoverSortSchema>;
export type DiscoverScope = z.infer<typeof discoverScopeSchema>;
export type DiscoverThreadResource = z.infer<typeof discoverThreadResourceSchema>;
export type DiscoverThreadListResponse = z.infer<typeof discoverThreadListResponseSchema>;
export type ListDiscoverThreadsQuery = z.infer<typeof listDiscoverThreadsQuerySchema>;
export type DiscoverStats = z.infer<typeof discoverStatsSchema>;
export type ShareTokenResponse = z.infer<typeof shareTokenResponseSchema>;
export type ResolveShareTokenQuery = z.infer<typeof resolveShareTokenQuerySchema>;
export type AdminGrowthTopSharer = z.infer<typeof adminGrowthTopSharerSchema>;
export type AdminGrowthStats = z.infer<typeof adminGrowthStatsSchema>;
export type AdminGrowthResponse = z.infer<typeof adminGrowthResponseSchema>;
export type AdminActiveUsersBucket = z.infer<typeof adminActiveUsersBucketSchema>;
export type AdminActiveUsersResponse = z.infer<typeof adminActiveUsersResponseSchema>;
export type AdminReferralsDailyPoint = z.infer<typeof adminReferralsDailyPointSchema>;
export type AdminReferralsTopInviter = z.infer<typeof adminReferralsTopInviterSchema>;
export type AdminReferralsAttribution = z.infer<typeof adminReferralsAttributionSchema>;
export type AdminReferralsAnalyticsResponse = z.infer<typeof adminReferralsAnalyticsResponseSchema>;
export type AdminOnboardingSourceRow = z.infer<typeof adminOnboardingSourceRowSchema>;
export type AdminOnboardingSourcesResponse = z.infer<typeof adminOnboardingSourcesResponseSchema>;
export type CritiqueAnnotation = z.infer<typeof critiqueAnnotationSchema>;
export type AdminCritiquePlaygroundRunRequest = z.infer<typeof adminCritiquePlaygroundRunRequestSchema>;
export type AdminCritiquePlaygroundRunResponse = z.infer<typeof adminCritiquePlaygroundRunResponseSchema>;
export type EmailTemplateField = z.infer<typeof emailTemplateFieldSchema>;
export type EmailTemplateSummary = z.infer<typeof emailTemplateSummarySchema>;
export type AdminEmailTemplateListResponse = z.infer<typeof adminEmailTemplateListResponseSchema>;
export type AdminEmailTemplatePreviewRequest = z.infer<typeof adminEmailTemplatePreviewRequestSchema>;
export type RenderedEmail = z.infer<typeof renderedEmailSchema>;
export type AdminEmailTemplateSendRequest = z.infer<typeof adminEmailTemplateSendRequestSchema>;
export type AdminEmailTemplateSendResponse = z.infer<typeof adminEmailTemplateSendResponseSchema>;
export type PlanTierSchema = z.infer<typeof planTierSchema>;
export type BillingFeatures = z.infer<typeof billingFeaturesSchema>;
export type BillingSubjectSummary = z.infer<typeof billingSubjectSummarySchema>;
export type BillingStateResponse = z.infer<typeof billingStateResponseSchema>;
export type CreateCheckoutSessionRequest = z.infer<typeof createCheckoutSessionRequestSchema>;
export type CreateCheckoutSessionResponse = z.infer<typeof createCheckoutSessionResponseSchema>;
export type CreateBillingPortalResponse = z.infer<typeof createBillingPortalResponseSchema>;
export type UpdateTeamSeatsRequest = z.infer<typeof updateTeamSeatsRequestSchema>;
export type CreditSettingsResponse = z.infer<typeof creditSettingsResponseSchema>;
export type UpdateCreditSettingsRequest = z.infer<typeof updateCreditSettingsRequestSchema>;
export type CreateCreditTopUpRequest = z.infer<typeof createCreditTopUpRequestSchema>;
export type CreateCreditTopUpResponse = z.infer<typeof createCreditTopUpResponseSchema>;
export type CreditPoolMetric = z.infer<typeof creditPoolMetricSchema>;
export type AdminCreditPoolsResponse = z.infer<typeof adminCreditPoolsResponseSchema>;
export type AdminPlanOverrideRequest = z.infer<typeof adminPlanOverrideRequestSchema>;
export type AdminBillingSubject = z.infer<typeof adminBillingSubjectSchema>;
export type AdminBillingOverviewResponse = z.infer<typeof adminBillingOverviewResponseSchema>;
export type ThreadVoteValue = z.infer<typeof threadVoteValueSchema>;
export type ViewerVote = z.infer<typeof viewerVoteSchema>;
export type ThreadVoteRollup = z.infer<typeof threadVoteRollupSchema>;
export type CastThreadVoteRequest = z.infer<typeof castThreadVoteRequestSchema>;
export type ThreadCommentAuthor = z.infer<typeof threadCommentAuthorSchema>;
export type ThreadComment = z.infer<typeof threadCommentSchema>;
export type ListThreadCommentsResponse = z.infer<typeof listThreadCommentsResponseSchema>;
export type CreateThreadCommentRequest = z.infer<typeof createThreadCommentRequestSchema>;
export type CreateThreadCommentResponse = z.infer<typeof createThreadCommentResponseSchema>;
export type UpdateThreadCommentRequest = z.infer<typeof updateThreadCommentRequestSchema>;
export type UpdateThreadCommentResponse = z.infer<typeof updateThreadCommentResponseSchema>;
export type DeleteThreadCommentResponse = z.infer<typeof deleteThreadCommentResponseSchema>;
export type ThreadCommentEvent = z.infer<typeof threadCommentEventSchema>;

// ---------- Profile types ----------
export type ProfileMiniUser = z.infer<typeof profileMiniUserSchema>;
export type FollowUserResponse = z.infer<typeof followUserResponseSchema>;
export type UnfollowUserResponse = z.infer<typeof unfollowUserResponseSchema>;
export type UserFollowListResponse = z.infer<typeof userFollowListResponseSchema>;
export type FollowSuggestion = z.infer<typeof followSuggestionSchema>;
export type FollowSuggestionListResponse = z.infer<
  typeof followSuggestionListResponseSchema
>;
export type ReferralReferee = z.infer<typeof referralRefereeSchema>;
export type ReferralListResponse = z.infer<typeof referralListResponseSchema>;
export type UserContributionDay = z.infer<typeof userContributionDaySchema>;
export type UserContributionsResponse = z.infer<typeof userContributionsResponseSchema>;
export type UserActivityEvent = z.infer<typeof userActivityEventSchema>;
export type UserActivityListResponse = z.infer<typeof userActivityListResponseSchema>;
export type ProfilePublishedProject = z.infer<typeof profilePublishedProjectSchema>;
export type ProfilePublishedProjectListResponse = z.infer<
  typeof profilePublishedProjectListResponseSchema
>;
export type PublishProjectResponse = z.infer<typeof publishProjectResponseSchema>;
export type ProfileImageKind = z.infer<typeof profileImageKindSchema>;
export type ProfileImageContentType = z.infer<typeof profileImageContentTypeSchema>;
export type ProfileImageUploadRequest = z.infer<typeof profileImageUploadRequestSchema>;
export type ThreadCoverUploadRequest = z.infer<typeof threadCoverUploadRequestSchema>;
export type ThreadCoverUploadResponse = z.infer<typeof threadCoverUploadResponseSchema>;
export type ProfileImageUploadResponse = z.infer<typeof profileImageUploadResponseSchema>;
